import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Activity, MessageSquare, Brain, FileText, ArrowRight, Layers,
    ExternalLink, ChevronRight, ChevronLeft, Cpu, Bot, Box, Database,
    Radio, ArrowLeft, User, Send, X, Code, WifiOff, Wifi,
    Microscope, Image as ImageIcon, ShieldCheck, Linkedin, Mail, Github,
    BookOpen, PenTool, Feather, Monitor
} from 'lucide-react';

// ==========================================
// === 1. DATA CONFIGURATION & HELPERS ===
// ==========================================

const COLORS = {
    cyan: { hex: '#06b6d4', glowHex: '#22d3ee' },
    purple: { hex: '#a855f7', glowHex: '#c084fc' },
    pink: { hex: '#ec4899', glowHex: '#f472b6' },
    emerald: { hex: '#10b981', glowHex: '#34d399' },
    blue: { hex: '#3b82f6', glowHex: '#60a5fa' },
    teal: { hex: '#14b8a6', glowHex: '#5eead4' },
    rose: { hex: '#f43f5e', glowHex: '#fb7185' },
    amber: { hex: '#f59e0b', glowHex: '#fbbf24' },
    orange: { hex: '#f97316', glowHex: '#fdba74' },
    slate: { hex: '#64748b', glowHex: '#94a3b8' },
    rust: { hex: '#ea580c', glowHex: '#fdba74' },
    sepia: { hex: '#d4b483', glowHex: '#e6ccb2' }
};

// Helper to create project objects
const createProject = (id, title, shortDesc, icon, color, longDesc, techStack, isOnlineStatus, link = null, isInternalRoute = false, images = [], type = 'project', fullArticle = "") => ({
    id, title, desc: shortDesc, icon, ...color,
    longDesc, techStack, isOnlineStatus, link, isInternalRoute, images,
    type, fullArticle, hasSubMenu: false
});

// --- DETAILED PROJECT DATA ---

const WEB_PROJECTS = [
    { ...createProject(
        'piwatch', 'PiWatch', 'Real-time IoT monitoring dashboard.',
        <Activity size={50} className="text-cyan-400" />, COLORS.cyan,
        "A dedicated IoT dashboard integrated directly into this portfolio ecosystem. It establishes a WebSocket connection to hardware sensors to display real-time metrics, charts, and device statuses. \n\nFeatures include live data streaming, device state management, and an alert system. (Currently running locally for demonstration).",
        ['React', 'WebSockets', 'Node.js', 'IoT Integration'],
        false, '/piwatch-welcome', true,
        ['/assets/projects/piwatch.png', '/assets/projects/piwatch2.png']
    ), authors: ['hamza'] },
   
    
    { ...createProject(
        'novatalk', 'NovaTalk', 'Seamless, privacy-first communication.',
        <MessageSquare size={50} className="text-pink-400" />, COLORS.pink,
        "A production-ready messaging platform engineered for privacy and seamless connection. Users authenticate via Google and can send friend requests to connect instantly. \n\nThe system uses Firestore snapshots for real-time message delivery without polling. Unique features include a 'Privacy-Friendly AI Assistant' that can draft replies and manage chats on your behalf.",
        ['React', 'Firebase Auth', 'Firestore Realtime', 'Google Cloud Functions', 'AI Agents'],
        true, 'https://chat-app-open.web.app', false,
        ['/assets/projects/novatalk1.png', '/assets/projects/novatalk2.png']
    ),authors: ['hamza'] },
   
    {...createProject(
        'reqsys', 'ReqSystem', 'Professor Request Management.',
        <FileText size={50} className="text-emerald-400" />, COLORS.emerald,
        "A comprehensive academic management portal. Students log in to view professor profiles and submit supervision requests. \n\nAdmins have a real-time dashboard to accept or reject these requests, which instantly updates the student's status view. Built with a local MySQL database for robust relational data management.",
        ['React', 'Node.js', 'MySQL (Local)', 'REST APIs'],
        false, null, false,
        ['/assets/projects/reqsys1.png']
    ), authors: ['hamza'] },

    { ...createProject(
        'mindmate', 'MindMate', 'AI-powered mental health assistant.',
        <Brain size={50} className="text-purple-400" />, COLORS.purple,
        "A secure, empathetic web application designed for mental wellness. It features a personal journaling system where entries can be saved and downloaded locally. \n\nThe core feature is a custom-trained AI model that provides supportive, context-aware dialogue to help users navigate stress and anxiety. Includes full user authentication and history tracking.",
        ['MERN Stack (MongoDB, Express, React, Node)', 'JWT Auth', 'Custom LLM', 'Python Backend'],
        false, null, false,
        ['/assets/projects/mindmate.png']
    ), authors: ['fahad'] }
];

const RUST_PROJECTS = [
    { ...createProject(
        'rust-seg', '3D Segmentation', 'Biomedical teeth & jaw separation.',
        <Microscope size={50} className="text-orange-400" />, COLORS.rust,
        "Developed core segmentation algorithms during a 6-month internship for a French dental tech firm. \n\nWork involved separating complex 3D structures from raw intraoral scan data, specifically isolating individual teeth and the mandible (jaw) for further orthodontic treatment planning.",
        ['Rust', '3D Computational Geometry', 'Algorithm Design', 'Medical Imaging Data'],
        false, null, false,
        ['/assets/projects/rust-seg.png']
    ), authors: ['asad'] }, // Unassigned
    
    { ...createProject(
        'rust-model', 'Mandible Simulation', 'TMJ rotation & movement analysis.',
        <Box size={50} className="text-orange-400" />, COLORS.rust,
        "Focused on the kinematic simulation of the Temporomandibular Joint (TMJ). \n\nImplemented logic for simulating realistic mandible rotations and movements to analyze jaw behavior. Included work on 'Stripping Biometrics' to analyze dental structures.",
        ['Rust', 'Physics Simulation', 'Kinematics', 'WGPU Rendering'],
        false, null, false,
        ['/assets/projects/rust-model.png']
    ), authors: ['hamza'] },
    
    { ...createProject(
        'rust-engrave', '3D Text Engraving', 'Boolean operations on meshes.',
        <Code size={50} className="text-orange-400" />, COLORS.rust,
        "Engineered robust functionality to perform boolean subtraction operations on high-density 3D meshes, allowing for dynamic text engraving directly onto dental models for identification purposes.",
        ['Rust', 'Mesh Boolean Ops', 'Geometric Processing'],
        false, null, false,
        ['/assets/projects/rust-engrave.png']
    ), authors: ['hamza'] },
    
    { ...createProject(
        'rust-occlusion', 'Occlusion Planes', 'Dental alignment visualization.',
        <Layers size={50} className="text-orange-400" />, COLORS.rust,
        "Created advanced tools for generating and manipulating occlusion planes using nine distinct manipulators, aiding orthodontists in visualizing and planning ideal dental alignment.",
        ['Rust', 'UI/UX 3D Gizmos', 'Linear Algebra'],
        false, null, false,
        ['/assets/projects/rust-occlusion.png']
    ), authors: ['hamza'] },
    
    { ...createProject(
        'rust-hooks', 'Bracket Hook Tech', 'Orthodontic feature detection.',
        <Cpu size={50} className="text-orange-400" />, COLORS.rust,
        "Implemented specialized algorithms for the detection and management of hooks on orthodontic brackets within 3D digital models, ensuring precise placement and collision avoidance.",
        ['Rust', 'Feature Detection', 'Computational Geometry'],
        false, null, false,
        ['/assets/projects/rust-hooks.png']
    ),authors: ['fahad'] },
];

const DATA_PROJECTS = [
    createProject('data-1', 'Algo Visualizer', 'Sorting & Pathfinding visualizer.', <Database size={50} className="text-slate-400" />, COLORS.slate, "A tool to visualize complex algorithms like Dijkstra, A*, and various sorting methods in real-time to understand their operational mechanics.", ['C++', 'React frontend'], false, null, false, ['/assets/projects/algo.png']),
    createProject('data-2', 'DB Architecture', 'Scalable Schema Design.', <Code size={50} className="text-slate-400" />, COLORS.slate, "Designing normalized and optimized database schemas suitable for high-traffic applications, focusing on data integrity and query performance.", ['SQL', 'PostgreSQL', 'Database Normalization'], false, null, false, ['/assets/projects/db.png'])
];

const AI_PROJECTS = [
    { ...createProject(
        'ai-threat', 'Threat Intelligence', 'Predictive Anomaly Detection.',
        <ShieldCheck size={50} className="text-rose-400" />, COLORS.rose,
        "Trained and evaluated over 10 different machine learning models (including Random Forest, SVM, KNN) on cybersecurity datasets to detect network anomalies and predict potential security threats with high accuracy.",
        ['Python', 'Scikit-learn', 'ML Algorithms', 'Data Analysis'], false, null, false,
        ['/assets/projects/ai-threat.png']
    ), authors: ['hamza'] },
    
    { ...createProject(
        'ai-mental', 'Mental Health LLM', 'GPT-2 & Llama Fine-tuning.',
        <Brain size={50} className="text-rose-400" />, COLORS.rose,
        "Fine-tuned large language models like GPT-2 and Llama on targeted datasets to analyze textual input and detect indicators of mental health conditions such as anxiety or stress based on speech patterns.",
        ['Python', 'PyTorch', 'Transformers (Hugging Face)', 'LLM Fine-tuning'], false, null, false,
        ['/assets/projects/ai-mental.png']
    ), authors: ['hamza'] },
    createProject(
        'ai-vision', 'Emotion Detection', 'Computer Vision-based analysis.',
        <Bot size={50} className="text-rose-400" />, COLORS.rose,
        "Developed a Python and OpenCV-based application to detect facial emotions using trained machine learning models, achieving ~70% detection accuracy in real-time webcam testing environments.",
        ['Python', 'OpenCV', 'Machine Learning'], false, null, false, 
        ['/assets/projects/emotion_detection.jpg']
    )
];

const EMBEDDED_PROJECTS = [
    createProject(
        'emb-fpga', 'FPGA VGA Display', 'ROM Image to Monitor.',
        <Monitor size={50} className="text-amber-400" />, COLORS.amber,
        "Using FPGA to display images from the ROM and displaying it to the monitor using VGA cable. This project involves hardware interfacing and timing generation.",
        ['FPGA', 'Verilog', 'VGA', 'Digital Logic'], false, null, false,
        ['/assets/projects/emb-fpga.png']
    ),
    createProject(
        'emb-pcb', 'PCB Design', 'Altium & Proteus fabrication.',
        <Cpu size={50} className="text-amber-400" />, COLORS.amber,
        "Extensive experience in designing single and multi-layer Printed Circuit Boards (PCBs). Mastering tools like Altium Designer and Proteus for schematic capture, layout, routing, and preparing files for fabrication.",
        ['Altium Designer', 'Proteus', 'PCB Layout', 'Circuit Design'], false, null, false,
        ['/assets/projects/emb-pcb.png']
    ),
    createProject(
        'emb-vga', 'ESP32 Wireless VGA Console', 'Retro console with WiFi controller.',
        <Code size={50} className="text-amber-400" />, COLORS.amber,
        "Designed a retro video game console using a single ESP32 to generate raw VGA video signals. It operates in Wi-Fi STA mode, hosting a web server that turns any smartphone into a wireless controller via HTTP GET requests.\n\nFeatures 6 custom-built games (Pong, Snake, Breakout, Space Shooter, Flappy Bird, Commando with AI states), an active buzzer audio system, and EEPROM high-score storage. Eliminated external GPUs completely.",
        ['ESP32', 'C++', 'VGA Graphics', 'WebSockets', 'HTML/JS'], false, null, false,
        ['/assets/projects/emb-vga.png']
    ),
    createProject(
        'emb-incubator', 'Smart Incubator', 'IoT control mechanism.',
        <Cpu size={50} className="text-amber-400" />, COLORS.amber,
        "Engineered a precise egg incubator using servo motors for automatic turning and a bulb-based heating mechanism controlled by temperature sensors to maintain optimal conditions.",
        ['Microcontrollers', 'Sensors', 'Control Systems', 'Hardware Interfacing'], false, null, false,
        ['/assets/projects/emb-incubator.png']
    ),
    createProject(
        'emb-hbridge', 'H-Bridge Design', 'Motor Driver Circuitry.',
        <Cpu size={50} className="text-amber-400" />, COLORS.amber,
        "Designed and implemented custom H-bridge circuits for robust bi-directional control of DC motors used in various robotics applications.",
        ['Power Electronics', 'Circuit Design', 'Motor Control'], false, null, false,
        ['/assets/projects/emb-hbridge.png']
    ),
    createProject(
        'emb-power', 'Dual Output Power Supply', '12V/5V DC & H-Bridge.',
        <Cpu size={50} className="text-amber-400" />, COLORS.amber,
        "Designed and built a regulated 12V/5V DC power supply and an H-Bridge motor driver circuit for DC motor control. Included Proteus simulation and hardware implementation on a custom PCB.",
        ['Proteus', 'PCB Design', 'Hardware Integration'], false, null, false, 
        ['/assets/projects/pcb.jpg']
    )
];

const ROBOTICS_PROJECTS = [
    createProject(
        'rob-sumo', 'Bluetooth SUMO Robot', '1st Place Combat Robot.',
        <Bot size={50} className="text-orange-500" />, COLORS.orange,
        "Designed and built an autonomous/mobile-controlled Sumo robot for competitive combat using Arduino, sensors, and a Bluetooth module. \n\nAchievements include winning 1st place at the COMSAT Tech Summit'24 (CTS'24) and Runner-up at Robian'23. Involves robust chassis design, enemy detection, and aggressive programming.",
        ['Arduino', 'Sensors (Ultrasonic/IR)', 'Mechanical Design', 'Bluetooth'], false, null, false,
        ['/assets/projects/rob-sumo.png']
    ),
    createProject(
        'rob-lfr', 'Line Following Robot', 'PID Control Implementation.',
        <Radio size={50} className="text-orange-500" />, COLORS.orange,
        "Developed high-speed Line Following Robots (LFR) utilizing array sensors and implementing PID control algorithms for precise and smooth navigation along complex tracks.",
        ['Embedded C', 'PID Control', 'Sensor Arrays', 'Autonomous Navigation'], false, null, false,
        ['/assets/projects/rob-lfr.png']
    ),
    ,
    createProject(
        'rob-lfr-fire', 'Fire Extinguishing LFR', 'Safety-focused mini-project.',
        <Radio size={50} className="text-orange-500" />, COLORS.orange,
        "Built a line-following robot using a custom IR sensor PCB, integrated with a flame sensor and fire extinguisher mechanism to detect and put out small flames using Arduino and embedded components.",
        ['Arduino', 'Custom PCB', 'Sensors', 'Robotics'], false, null, false, 
        ['/assets/projects/lfr.jpg']
    )
];

// --- CONTENT WRITING DATA ---
const CONTENT_PROJECTS = [
    { ...createProject(
        'art-harsh', 'The harsh evenings of winters', 'Beauty and melancholy.',
        <Feather size={50} className="text-yellow-700" />, COLORS.sepia,
        "Exploring the instability of calmness and the emotional impact of winter evenings.",
        ['Winter', 'Nature', 'Melancholy'],
        true, null, false, ['/assets/projects/rob-sumo.png'], 'article',
        `The harsh evenings of winters-
Winters....
A combination of sentimental peace and grace. it's an instability which when hits indirectly to mind makes us more stable.
The instability of Calmness -
The instability of inner Peace -
The instability of elegance -
The instability of undefined happiness -
The instability of comfortness -
The instability of strength -
Word ' instability ' because it captures us between extreme and topmost levels of excitement.
Starting off...
The extreme lavishing scent of morning breeze which captivates us to become its prisoner.It makes us slave to just feel that moment of joy.And we can't just ignore this because of the attractiveness it carries.it continuously emits that radiation of happiness which no one can ever resists. Following with the afternoon's dazzling sunlight which causes the little laziness inside us to sit .
The time between afternoon and evening which causes the peaceful impact on our hearts,the pleasant vibes which excretes from the wind makes us motivated to move on with our life.
The warm blankets at night which makes us cozy,let us feel the warmthness of pleasures.
Then it comes, for which I think winters are made for...- the rainy season - .
<<<IMG:/assets/projects/winter.png>>>
The critical licence to witness something extraordinary.
The vibes, the feelings the excitement it carries, I think one of the particular reason for which one must survive in this annoying world.
So in the very case of this ... imagine of scene when it's dark rainy weather around you, you're sitting in your home
turned off your light, let the dark and dull rainy weather's light illuminates and lit up your room,turned on your heater the mixture of dull light and heater's little luminescence make your room a little heaven and you continue witnessing the tiptoe raining.ahh
imagine Then going up on your balcony with a coffee mug in your hand , sitting just in front of your balcony fence , then that one little drop of rain from far above atmosphere choose to hit only your fence,and gets splits up on droplets that sprinkles on your face , asking you then...
what is stress? what is grief..?
<<<IMG:/assets/projects/rain.png>>>
and you simply reply .. what's that...?
Believe me no one ever in this whole world is more talkative than the nature itself and ,than the rainy season.This talkative mood of nature simply heals every wound,kills every tension makes you feel invulnerable.
It nourishes the mental Peace as well as your inner soul.It let's you enjoy that wholesome moments full of emotions which you gained in exchange of happiness and love. It adds that happy factor in equation of your life which can change the statement of ongoing life. It scripts your life again but this time with the golden pen of joy and happiness.That dull black rainy weather when talking to you, you can witness yourself in paradise .it synchronizes you and peace in memory of love.
Everytime this fascinating view,the scenery it creates,always cause a very impactful behavior in humans.<<<IMG:/assets/projects/nature.png>>>This Inexpressible love , hailing of emotions,storm of feelings I can't just express.

That momentarily feeling every second is worth more than whole world.
But all of this come to an end when something horrifying come to action.
Even though of Happiness, joy , peace elegance calmness still they become a easy subject to eleminate.
This whole frame of joy, inner peace love beauty ,essence , unforgettable breeze, sentimental calmness , is nothing when this terrible thing take place - the harsh evenings of winters- <<<IMG:/assets/projects/evenings2.png>>>
what we have always heard , happiness dominates the sadness but in this case it's not true.There is something unusual in these evenings making all of these moments to be forgotten.
They carries the coating layer of sadness with them. You know when frequency of two objects matches it causes resonance in it, exactly like this phenomenon,these evenings carry waves of worriness which penetrates inside people making them look sad and worried. This becomes even more disturbing when it coincides with people hearts making their appearance look sad even if they aren't.
Apparently, it just has that nature which causes a total disturbance in beautiful view of sunset.Some people feel very relaxed when they see sunset,and yes why not it is that but after sun sets,it denies the absolute view and causes mental disturbness. Our nature is very moody, its sunset poetry some time collaborates with happiness and sometime it invites these evenings to come and violate calmness of life. I don't know why but this ordinary day evening has lot more impact on daily routines mood. with it's one flow it causes people appearance to be sad as well as making most of them sad a little.
I was sitting in a class room ,then saw the sun set,yup quite fascinating,but after when this evening hits, I noticed quite silence. The silence which create that impact ,the environment which causes everyone to be sad .That stillness,the quiteness which causes everyone one to show theirs sad nature .It gets more scarier when it can let all people to appear as sad
.<<<IMG:/assets/projects/evenings2.png>>> 
I always question evenings why ??? Why you can't see happy faces of all. Why you can't bear the limited happiness. why you become so vulnerable?
why this disturbance.?
Why you kill the imagination, the imagination where everything was going absolutely fine.
why the drastic tension that makes everyone one suffer?
why the hyper stress that causes every one to be a victim of it.?
why are you that depress evening in which no one can express.?
why you blows that sad breeze limitless and make happiness limited.?
why you act as reverse catalyst for emotional equation of life.?
OR 
it just all appears to me as I can read people emotions from their faces.
If any time ,human sets some laws to be happy,the first violation will come through these evenings.
They collide with human psychology in such a way causes consistent nature gets transformed into demolished mood.`
    ), authors: ['hamza'] },
    { ...createProject(
        'art-voice', '~unknown Voice', 'The melody of a voice.',
        <Feather size={50} className="text-yellow-700" />, COLORS.sepia,
        "A piece on the impact of voices on the soul.",
        ['Writing', 'Reflection'],
        true, null, false, [], 'article',
        `~unknown Voice ~

Voices sometimes can be so soothing that it creates an impact on our heart, penetrates through our soul, nourishes our mental peace.It defines that environment,that let our eyes say what our tongue can't ...let us feel what we can't imagine,let us to be in that delusions , from which we don't want to get out.

Put us in such an imaginary world where everything is going so nice that we want to be a part of it forever... it's the voice we stumble upon that has a deeper connection with our soul,commonly called as serendipity..In symphony of love, Voice is responsible for emotions..Ever listened to such melodious vocals...Ever become a subject for someone's poetic words that cast a spell which envelopes you?

Ever encountered that voice ?? Ever felt that sound ??

Ever confronted some one with such importance for you...?Ever get stucked in trap of voice that let your eyes being able to talk...Yes!...Voice....`
    ), authors: ['hamza'] },
    { ...createProject(
        'art-trap', 'The Trap of uncertainty', 'Happiness vs Deception.',
        <Feather size={50} className="text-yellow-700" />, COLORS.sepia,
        "An article about the deceptive nature of happiness and sadness.",
        ['Philosophy', 'Life'],
        true, null, false, [], 'article',
        // PASTE YOUR RAW ARTICLE HERE. THE CODE WILL HANDLE THE PAGINATION PERFECTLY.
        `The Trap of uncertainty
In this life, what we crave and what we thrive for , is happiness. Everyone is basically behind this thing to achieve happiness, means the goal is same but the act through which they get the actual outcome , is different . For someone getting money is real happiness as they can buy what they want , for someone be good with others , and for many others they get happiness in different form. Despite the goal is same , but still the blockage is there for most of people , and the thing is we are stucked in trap of uncertainty. This world , the things around us , the icons which we have considered as our goal to be happy are all uncertain. Everything around us is just the deception. An uncertain feel , which feels so real and we are just blindly following. How unreal it feels , even when everything is uncertain, still we ignore the thing which we crave for and chose what? The perfect enemy of what we desire , the "sadness". If we just make sure to remember ; it is all deception of time , bcz it is running continuously, everything happiness, sadness is limited, even then we chose opposite of what we desires.  It plays with our mind by creating delusions that provoke our mind about lasting of any thing much longer. Both terms are mixed up in such a way , we want some other  and we choose other. How we fell in these, we want to be happy but this deceive us by fake visual which eventually made out mind as , to be happy, you really need something extraordinary otherwise nothing special in this life , and following this ultimately u will learn , what we chose after falling in this trap, is pure uncertain , u suffer to get some joy at the end but then that joyous moment dont even last that much and why that? because u never defined the tenure for suffering. In this way when there's no deadline for your sadness and problems, they become the reason of your definition. The answer for define yourself, then u will find , it will all the problems definition of your self , you lose yourself and problem speaks on your behalf.  Humans definition should be something that contains emotions, joyous occasions not the bunch of problem, sadness that become a representative of ourselves. See how uncertain , it shows us a bigger picture of all the sadness around us and signal out the mind that this is something real, our mind being a defensive driver , goes for this, ignoring that uncertainty, then we suffer in this and at the end , we come to know we wasted too much time on a thing that has nothing to do with our time being spend on this sadness. Now even each thing is uncertain, but still sadness , griefs,&  problems feel like everlasting because uncertainity only plays for happiness, that's why I called it as trap , when each thing is uncertain still our mind deceive us by showing happiness as unachievable and sadness as everlasting thing , and when we think sadness is about to end , it make it something that feels like a neglectable thing,  sometimes after that much suffering we don't deal happiness happily or sometime u r very late to get out of this suffering.A perfect example here of a person, bought some stock and some price with everything he had , when he used everything in terms of money property he had on this stock, from the time he bought its price keeps on decreasing at constant rate. This thing continues till 10 days and at 11th day the price became surprisingly increasing, and it goes to 10x of it's initial value . This thing was too much for his heart and his heart stops due to sudden wave of happiness and he died. Now see, form the time he bought till he died he had 10 days, his goal was to get  this eternal happiness as if it goes according to his plan, but still in this 10 days he was only keeping the sole enemy of what he really wanted the anxiety depression and sadness. This is what uncertainty looks like, we dont know the exact tenure still we sacrifice what we actually desire . See how uncertain is the happiness it self when he finally found , his soul was tired of constant suffering.
We all are somehow somewhere, trapped in this. No one know how long your goal of life ( happiness) will last , still we chose something that opposes the happiness. Yhis all is the game of balancing the life , coming out of this trap , teaching our mind and heart yo collaborate for what they actually desire , not for something that act as a blockage for u and happiness.We all have bounded happiness with something most valuable we achieve, although it's the cheapest occuring thing. Only thing is we have to get out of this uncertainty trap, when everything is uncertain why to prioritize the opposing force, why not to enforce your mind to seek what it actually desires. It not that simple there are time,.places when we are actually sad , burden of responsibilities, many thing around just what we need to define a tenure of all , not letting like if we have 10 days we spent all depressed soul, and then crave for 11th day which who knows maybe the last one for you. When even your most desired thing has no guarantee of lasting then why not to be egoistic here , the thing which is stopping you from getting your stuff, you still chosing that thing for days and waiting for your 11th day. Uncertainty is there for each moment, we have to extract the best out of it, extract each point of happiness form each moment to collectively achieve what is yours (happiness).`
    ), authors: ['hamza'] },
    { ...createProject(
        'art-standards', 'The standards of happiness', 'Levels of joy.',
        <Feather size={50} className="text-yellow-700" />, COLORS.sepia,
        "Defining the three standards of happiness: Sporadic, Pervasive, and Exalted.",
        ['Psychology', 'Self-Help'],
        true, null, false, [], 'article',
        `The standards of happiness
Peaceful mind leads to peaceful heart which eventually makes your life a little paradise. In order to live in this heaven, you must define each moment as a cheerful moment dipped in ocean of happiness. you can't deny the fact that to call life as paradise,each moment carries too much importance just like individual drops collectively form the vast ocean exactly each happy moment make your life a living paradise.
So before proceeding, Go out in defined public, and laugh out loudly u'll notice people will laugh, and after some seconds of embarrassment u'll also start to laugh at ur own embarrassment. This is the happiness standard which allows you to be Happy at every stake. Apparently, there are three standard of happiness.
Sporadic Happiness 
Pervasive happiness 
Exalted happiness 
Sporadic Happiness refers to what that is rarely felt. People living in this standard are kind of one who are fed up with their life as they laugh and smile at irregular intervals or very rarely. These types of people limit and find themselves in constant state of disappoinment, searching for joyous moment which drag them towards another sad element.
Pervasive happiness refers to what that is felt quite few times but not always. People In this standard limit themselves to laugh when they will achieve particular thing they want untill then,a constant deprived mood is their station.These people lives thought wise, like if this particular thing we get, then smile.
Then there comes the third standard which I think is most beautiful and iconic. The standard which should be acquired by every human being. People in this standard live their life moment wise; win the moment which your are in. Enjoying each moment with happiness.
You should understand there's always a difference between existing and living.
You should make every moment,Worth your presence. There are two types of people:
One who are living the life as they are forced to live i.e remain alive . 
And second who choose to get cooked in the pot of happiness.
Actually, it's very easy to live in third standard, and actually , people want to live in this but the thing that resist them from doing so is not defining the duration for problems in life. Yeah there are problems always but if you don't define the tenure they will always cause instability in inner you.U should limit your problem that enough thinking about these, thought enough ,let's move on. Don't be the prisoner of thoughts, break the prison you don't have to live in the prison always time to flee, time to escape. You know why everyone in life is So called looser ,not bcz of losing anything it's because we keep on thinking about the things which actually aren't worth visualizing.We always choose to be a prisoner of mind , living in thoughts giving time to useless problem and for what ,in the end there will be just you with burden of thousands of tears and destructive future...Life is actually like a bus every one on this will leave you on their station so why not to make some memories that will be worth memorizing in their absence or for them in your absence. we need to understand the human psychology ,untill we Don't understand about something getting away from us we don't value the value, or when we get to know about anyone's absence we actually start caring for them. So enjoy the moments now so you don't regret after...Make memories in every moment and keep them in your heart.The problem is we treat moment of happiness as weeks while moments of sadness as decades. Sadness won't get eliminated untill you thrive to do so, yes happiness dominates sadness but not that easy , if you haven't defined the tenure for problem then a sudden wave of happiness won't actually kills the sadness , you have to standardize it in time line just like little drops collectively called as ocean similarly every moment spend in happiness will collectively make you happy. It's not that suddenly happening thing , go with your closed ones that meant to understand you and talk for every enjoyable moment. Take a deep breath, and call your lord...tell every problem, pray and smile it's common practice of our beloved prophet (S.A.W).
Responsibilities never meant to make you depressive, they just make you a more confident on your smile, change the ideology. 
If you keeps on remembering the problems and crying over them won't actually eliminate them, keeping problems in mind acts like virus they kill the happiness inside also , so u should add catalyst to boost up the happiness and that catalyst is the momentarily joy to which you don't give time...
Remember no one will wipe your tears , they'll come see you in pain , ignore you and maybe some one will stop for you to .... record you ,no one will come to console you , no will offer solace during difficult time. Never ever depend upon others or don't let the situation for you to make u depending on others , before the tension and grief dominate, eliminate them by happiness.
Yes there are some who will look after you but why to be depending on them why not to strengthen our self in such a way that we don't need anybody to rely on.
And most easy thing to do this is to be in exalted happiness, and believe me it's very very easy . Cause u've seen this we don't need any thing in particular to be happy. Happiness actually has very low standards you see you can laugh at your own embarrassment,even some time our failures make us happy, make us smile ,make the disturbed mood to transform into solid stabilized face. Then, Why not to be a icon for others?
why not to be a element of ecstasy? Why not be be a reason for other to be in Felicity..?
why not to become a factor to make others happy?
why not, not to be Happy yourself?
Why to live in drastic depressed environment?
why to act like a victim for everyone to come and disturb?
why to become a prey of persecution?
Why not to adore who are ours and ignore the hypocrites?
why to rely on other to be happy?
Why can't we achieve peace ourselves?
Peace isn't called to be happy when surrounded with problems, I define peace as; when You don't need anything to be happy, and nothing make you sad . with an empty heart if there is a smile on your face you are in peace . in real it's an another topic...
So let's get out of this depressed environment let's enjoy every moment, remove the ones who cause tempting behavior, do all childish things ,get involved in all fancy behaviors enjoy your life as good as you can cause no one waits neither no one will be there to make u happy. they'll notice, ignore and will go busy in their routine..
if you are till here , there should be a question in your mind as a reader if you resonate with all above or even something, Does happiness has types ? So basically, no, above-mentioned are not types they are stages in which different people lies... due to their lifestyle. This is not just read and go.. I want all of the ones who are reading this , You should Make changes for yourself , Enough living for others, now it's time to live for yourself...
You hold the reins to your life; don’t let the opinions of others become the cause of your tears.Remember it's your journey and no one will wait for you , you have to face alone. Just like light shines up the dark valleys similarly your inner strength will scintillate your disturbed mood, what require ; is to stand up for yourself .
Remember crying over a lake doesn't mean that it's a lake of tears, so distinguish between the one who cried and get drowned in it's own ocean and the one who just came and quickly get one's place,some time you should add your tears to the ocean also as the last tear and buckle up for next mission for your life , be happy ...spread happiness... That'll work for you ...keep smiling...`
    ), authors: ['hamza'] },
    { ...createProject(
        'art-inner', 'The pursuit of inner joy', 'Focusing inward.',
        <Feather size={50} className="text-yellow-700" />, COLORS.sepia,
        "Prioritizing self-happiness over external validation and breaking free from self-doubt.",
        ['Reflection', 'Inner Peace'],
        true, null, false, [], 'article',
        `The pursuit of inner joy
Happiness is a fleeting concept. We often find ourselves reminiscing about the past, recalling moments that brought us joy.
Because Some moments are real definition of peace when they resonate with the already happened happiness. It's like extracting happiness from any moment and when they get the compatibility with past. Childhood, in particular, was a time of unbridled happiness. Every moment was a discovery, filled with wonder and awe.
It was so awesome that each moment was full of happiness, each moment we crave to extract happiness and we do so
As we grow older, responsibilities weigh us down. We become increasingly uncomfortable, The sense of responsibility arise and make us a bit uncomfortable to enjoy every minute of life. I usually, get the feeling of past little happy moments in very uncausual way , some time the smell let us remember some time , some taste brought us back to place , to time where each and every thing was fascinating.
As you  leave the train of childhood and enters next station of adulthood, u may notice that somewhere when we left that station, we lost the power to extract that happiness from each moment, because whenever we do so, the devil in form of mind exacerbate the situation by calling what am i doing?
Am I making the ones around me happy enough?
Am I just wasting the time ?
And these questions keeps on increasing, and if  we do anything to let our brain to be quite, the more  hyper it gets.
Brain is not easy thing, it sense thing as harmful or good once it's signalled about something and then we feel a sense of burden. it gives that thing a tag of " harm" and by doing so we get stucked in loop of questions where even answers are carrier of new questions.
The problem is the more you feel for others , the more you get stucked in loop of infinite thoughts .when we set up to think what particular thing is making us, uncomfortable or sad , we get to know it's nothing, or" I don't know" ,but that thing keeps on teasing mind from inside.
Now to get out, eremitism is not the thing to be acquired because question will keep on increasing again and again and the loop of others ,why they aren't happy, Expands. One thing everyone need to know the more you try to make others happy the less happy u become.
To break free, we must acknowledge that prioritizing others' happiness over our own can be detrimental to our well-being. Recognizing this, we can begin to shift our focus inward. By prioritizing our own happiness, we can silence the constant questions and doubts that plague our minds. It's essential to accept that we can't pour from an empty cup; we must fill ourselves with happiness first before we can truly make others happy.
And the thing is sometimes we should admit this, that okay  am not tdoing that well to let other happy & and that's okay , now this thing doesn't mean you are wrong basically it's self rehabilitation from mind bcz once you accept the thing that okay not doing well enough but for whom ? Others... right then I shouldn't care .
This thing manipulate the sense of thinking and make that very moment transformed in genuine relief .
The world is selfish, enough selfish every one see for themselves, put their self on top then they think about you, then why to add the extra "feel" element for others when there is yourself, which is in diminished timeline.
Focusing on yourself is way more important than to living only for others.
Somewhere before was against the fact of living for yourself but at someplace and sometimes you should focus on yourself .
What make us dead inside is focusing on others and Relying on others. once you get out of this loop, u will see scintillating happiness awaiting in form of that same others which you want to make feel good so it's all starts from you,
Prioritize some time the inner you before others.Then others will automatically be yours...
Think for yourself...
Rediscovering happiness requires a shift in perspective. We must learn to prioritize ourselves and recognize that our own happiness is essential. By doing so, we can break free from the cycle of self-doubt and find genuine happiness.`
    ), authors: ['hamza'] },
    
    { ...createProject(
        'art-eyes', 'The eyes عجیب', 'A poetic glance.',
        <Feather size={50} className="text-yellow-700" />, COLORS.sepia,
        "A poetic description of eyes as a dense forest and a mirror of the soul.",
        ['Poetry', 'Urdu', 'Observation'],
        true, null, false, [], 'article',
        `The eyes
عجیب ہے انکا اندازے گفتگو 
زباں نہیں انکی، نگاہیں بات کرتی ہیں 
اشاروں سے انکے بدلتے ہیں شب و روز 
نگاہیں ہی، رات کو دن اور دن کو رات کرتی ہیں
Eyes..A dense forest where most of the people go and lost.For some ,they are the whole earth and for someone's earth they are universe.Home for most of loved ones, while residence for all of the lost ones. Sometimes a beautiful glacier where happiness is Falling, and sometimes a scintillating lake where tears are crawling. One word to describe the beauty , on the same moment an ununderstandable book. Some time a world for some, while sometimes defining immortality for many.. Factor of someone's happiness while sometime the reason for someone's departure.A memorable book for many but a thoughtful tears for some...The eyes...`
    ), authors: ['hamza'] },
    { ...createProject(
        'art-visuals', 'The deceptive visuals', 'A tale of perception.',
        <Feather size={50} className="text-yellow-700" />, COLORS.sepia,
        "A story about perception versus reality, featuring a girl, a boy, and a tree in the snow.",
        ['Story', 'Fiction', 'Perception'],
        true, null, false, [], 'article',
        `Visuals aren't the same thing which we see .They Often deceives us with attractiveness. Every beautiful thing isn't gold, we have Heard this . Lets dive in story related to this. A simple girl named 'sul' who loves to explore, reading a book in calm sunset ,the sun just about to set, half cloudy weather and slow wind is blowing.Book describe an unknown title with no description. Sul read that book too much emotionally connected, thinking she's main character. the book:
A winter season, with heavy snowfall which is now just at reduced speed, With cold breeze which shivers any human. Dark clouds ,on a road ,surrounded by dense forest in which trees shedded their leaves, just branches which are partially white due to snow, a boy passes by, noticing a girl standing in front of one of the leafless tree as she is talking to it.
girl's hair was almost dipped in white, Still smiling. Boy who loved the scenarios like this regarding weather ,who just stopped to glimpse the snowy weather now was intrigued . Girl was transforming into a snowman slowly untill the boy approached and asked:
"What are you doing here alone, in dense snowfall?"
Girl (in an unknown expression):" Am taking to an old friend!"
Boy: "When nature is talking,we r supposed to be the one who feels not the one who talks"
Girl: (noticing the boy's car):"M not impressed by wealth, u may leave,you are causing interruptions "
Boy(in curiosity):" By the way ,with whom you are taking to.?"
Girl: "Humanity!!"
Boy: "you find humanity near abandoned tree??"
Girl(Smiling): " isn't it wonderful? THE LOST legacy of humans is only available here"
Boy (deprived): "Lost??? Humans and humanity are linked together,one can't survive without other"
Girl(in sad mood, get close to boy And said slowly): " Among human and humanity there's a barrier called emotions, feelings which can't be described with wealth "
Boy(winning): "oh...so... a weaker thing can't be a barrier, it's called hurdle, hurdle are meant to separate while emotions tend to connect ...Tell me which one are you familiar with ? What brought you here hurdle or emotions??"
Girl: (turning back to tree): you know Among all trees I only find this tree as green with fruits..You know why?? Because hurdles make all others leafless,while emotions make them fruitless"
Girl(then plucked a fruit from the abandoned tree): "See how beautiful it is..."
Boy (Shocked): Where does you get that fruit "
"From this tree" says the girl.She further said " Look Humanity...."
" Yes see Humanity is dwelled into the Reddish Apple... meaning the humanity will find its own way ,emotions can't be a blocker"Boy said as he is winning the argument.The Girl rapidly cut the apple into two pieces and showed it to boy..." This is how humanity deceives... Inside with rotten all..& what was you saying.... humanity find its way???"
Boy( who loved the poetic discussion) took apple piece from her and place it above in skies the apple became white and wasn't showing any sigh of rotten fruit. Then said " Humanity is not solely thing ... every thing comes with problems...see snow and humanity covers the bad aspects of apple..one will easily say humanity saves the apple bad figure...It's rotten nature is sandwiched between snow and reddish (humanity)."
Girl (in furious mood):"Eat it then if it covers bad aspect ,eat it "
Boy hesitated... And before he spoke Any thing girl ate it and said..."You rich people can never understand apple was never rotten your eyes betrayed you, similarly as you betrayed yourself talking to me"
Boy: "Poverty or Richness isn't described by the wealth,its heart who feels and brain who thinks."
Girl:" Then why you hit the poor boy yesterday? Where was heart then?"
Girl turned back as she is going but stopped and said: " You maybe good a person but humanity is far from you"
Boy(shocked) "I wasn't in this city how you know about last day"
Girl: "I know more you are reading this and don't understand what is written"
Boy looked on her car then back saw the girl was going , he. Called her but she disappeared quickly in snow forest...
Months later, the boy returns to the leafless tree, seeking closure Surrounded with thought with echoes still in mind "What happened that day who was her.?"
He approached the same tree but no one was there...He hold down himself for sometime, wishing for her reappearance... Suddenly he heard a noice behind him.
The girl reappears, he was glad and shocked at the same time and they rekindle their conversation. Girl said : " So Your curiosity made our reunion happen, What do you say now ? Emotions are the barriers or not..?"
He apologizes for his past mistakes and proves his commitment to change.
As they walk together, the girl hands him a small package saying "I have a present for you" .Inside, he finds an apple seed.
"The apple's seeds hold the truth," she says. "Plant it, nurture it, and you'll understand.
The boy Accepted it heart filled , He nodded his head down but before he says anything the girl was gone again , but this time he was happier than before because girl voice was whispering in his ears continuously " Your soul is pure , You came back , words You said before was right, You are actually good-hearted don't ,don't turn away now.."
HE did the same as the girl said when handling him the present, and realized:
1. True humanity lies beyond material wealth.
2. Emotions connect or isolate; choose wisely.
3. Growth requires self-reflection and empathy.
But still he didn't know it who was her ? Why she give him an important message?
But strange occurrences start to happen:
- He finds cryptic notes with quotes from their conversations.
- He senses someone watching him, always just out of sight.
- The tree's growth accelerates, as if driven by an unseen force.
Years pass; the tree flourishes. The boy, now a wiser man Trying his best to find out the truth about her . Went many times to the tree but didn't find her, when he lost hope about meeting her he lean down with the tree he planted and asking himself, how a small incident changed him but he will never stop to find out who she was... Suddenly he heard some footsteps,on turning back he realizes it was same girl who reveals:
"You've bridged the gap between humanity and emotions. But there's more to learn."
Before he asks girl said " Sometime it is ok to not ,interfere and try, to find out some hidden Truth. And if curiosity hits you too much , I'll tell you at right time till then Be a symbol of peace"11
As they vanish into the snowy landscape, the boy notices:
The tree's branches now bear fruit – apples with seeds, symbolizing the cyclical nature of growth, humanity, and understanding.
Suddenly, the girl's voice whispers in his ear:
"Your journey is far from over. The truth is hidden in plain sight."
The boy turns, but she's gone. He's left with:
A faint scent of apple blossoms.
A cryptic message on the tree: "Look closer."
Boy took a deeper breath, and tried to look deeper inside he realized what;
What he was and now how he is changed . Previously he was just a icon of disturbance, too much ignorant regarding money, thinking wealth will give him eonin peace, he can rule on any thing.But now he is far away from this he used to spend most of his time with poor ones , in his bad days he used to see the apple and says: "Humans never wanted to rule it's money who want. So money approaches Us and realize us first, money is our Every need.For some time we kind of agree but then greed keeps on increasing, making us cruel. Then we start by ruling on poors and slowly money takes full control.We should understand , being wealthy isn't a good thing , a good heart who feels is everything. Who ever she was I don't know whether we'll meet again or not, but she taught me, humanity,the message which no one did in whole life,and I know we'll meet again...I know..till then remembrances and thought will be my residence "
Rain started he continues to sit there and feel nature once again as he loved the rains.....Sul who was reading this، kind of depressed reading this , she was on a hotel room went to the window and witness, it was raining there also..with loud sound of spark and thunder , the lightening was frightening everyone there, but she was enjoying as she just read about the boy who chose to sit in rain in depressed mood. She kept on watching the rain without uttering a single word , melancholy (sadness) was shining in her eyes, still she was enjoying the rain. Suddenly there was a knock on her hotels room , she went out and saw a girl in hurry said someone left a present for you she showed her the address and identity, sul replied " Yes it's me "
As the girl who came to give the gift was about to leave, said don't be sad it was just a story, sul (who
Was verifying her address ) shocked ,her eyes were kind of stunned, she quickly saw above to the lady but she was there , sul rushed out to see but she wasn't there also . Somehow she collected her self and went back to her room to check what's on present. She opened the present , and as she saw the present she was almost paralyzed...She saw the apple seed with some notes narrating exact message what she read before...with an additional note" Humanity lies inside heart , books are meant to spread positivity.The icon of bravery lies within let's eliminate the disgusting disharmony"
She read it and went to sleep...as she didn't understand what happened....
(Story...ended here with unknown expressions and some un answered questions maybe in next part)
(Writer don't know also....🥲)`
    ), authors: ['hamza'] },
    { ...createProject(
        'art-reliving', 'Reliving the life', 'A perspective on life.',
        <Feather size={50} className="text-yellow-700" />, COLORS.sepia,
        "A call to live life fully, enjoy the moment, and not just survive the grind.",
        ['Life', 'Motivation', 'Social'],
        true, null, false, [], 'article',
        `Reliving the life
From chirping of birds in morning till silence of stillness at night,
from calmness of breeze in morning till loneliness of peace at night,
one thing keeps on whispering in my ear...life ..life ...as if it's keep echoing in background , desperate for attention.
I've noticed quite disagreement about this sensational phenomenon.
most of people's are just spending the life as they are forced to live. No...please why at least? Just question your self why you are suffering alone...
means yeah there are some problem definitely there are ..but that's not entirely your fault,why you are making yourself a prey of this persecution.Yeah I know about surrounding's qq7tensions...griefs .. disappointment but still why are you suffering alone.There are lot of people always involved about the problem around, but when they are happy at least try to get some motivation and live happily. Just a very small amount of years we all have and no one knows how much still we choose to remain in this drastic condition, no hangout no enjoying just putting blame on yourself and not moving on.
if you are not satisfied with some thing then definitely you are on a higher verge at something else ,
if suppose you only have eyes then ,these eyes have right on you to see accurately whole body of ours is in our guardianship, we have to take care.. . So surrounding with enormous things for which we have to play correct role,then why not to enjoy this very moments, with friends family . Take some time to laugh with people who matter..
Give them some time enjoy limited days of life spent it according to the fundamental balance with family , with religious activities.
Don't let yourself a solely responsible of something that is just for this world, come on we have a very long journey after life, so focus on that, focus on yourself , focus on your life .
Because you are not alone in this there are many people linked to you. You know humans are social animals.we have to interact with others talking with them, working means we are surrounded with many responsibilities so we should not just focus on ourself.
Yeah this is meanful,to live in tension live separately and blaming to that particular reason which has forced you to live like this, come on change your self.
This is one aspect there is other where some people choose to live alone separately.
You should understand being human we can't live like this we have to socialize, in this small life why you don't want to live together enjoy and be happy.
Reading somewhere, about this pathetic lifestyle about living alone trying to show this like attitude, watching others , small conversation that .....talking less will let you look like a impactful. NO a big no is the answer no one looks better like this. Reality is different we have to live happily, socialize.
I was playing game and read somewhere,
if it's worth dying at time of war then it should be worth living when at time of peace"
believe me this is just not only a quote it's more than that.
it is full script of life ,that come-on if at war,at fight we have to fight and save our country,we have to show patriotism,then at peace means at time where there is no war not fight then it is our first responsibility I'll say to live happily,show patriotism ; here patriotism is defined with. little difference ,here it means "enjoying and living".
The word " living " isn't just telling us to remain alive ,it means. we have to live happily ,live in peace, live with dignity.
Any problem,tell that problem you are not big enough than Our lord Than our creator,
We will live this limited life happily then proceed further,we will not let you to ruin the single moment of this life . we'll love ,live and enjoy and will rely on our lord our sustainer to eliminate that namely disturbance that is shaking us and tempting us .
So try to live happily and in peace ✌️✌️...
(Old)`
    ), authors: ['hamza'] },
    { ...createProject(
        'art-time', 'How the time changes', 'A nostalgic reflection.',
        <Feather size={50} className="text-yellow-700" />, COLORS.sepia,
        "Reflecting on how time changes friendships, emotions, and maturity.",
        ['Nostalgia', 'Friendship', 'Growth'],
        true, null, false, [], 'article',
        `How the time changes .. .
I just remembered that time 
when...
it was only about..
only fun nothing else
say any thing without thinking
didn't need to hide something
nothing like"now I am changed"
no one even get brothered by asking any thing..
no one minds any thing
the time when there was no mean 
when everyone just reflect...
when everyone used to share with others...
when there was un countable love ...
when calling someone"friend"was common
when all deserve to be called a "friend"
when All know what is "friend "
when no one hide their greifs...
when happiness was common to share
when we don't think that what benefit might we get if we do any thing else for someone....
.
.
.
Ahh
Looking back,
just remember the time...
the time ....
when we used to stay together...
talking irrelevantly ...
talking abruptly....
talking senseless...
.
.
.
with hindsight...
that time when all friend...
felt no hesitation to talk or to say something....
All were free from every tension and greif...
.
.
.
.
..
.
.
.
.
.
.
.
.
.
.
.
.
.
.
.
.
.
.
.
.
.
I was just talking to a friend...And the way he talked...I just remembered...
the time ...
how it has changed...
I will not usee his name but I remembered...
the way how we used to talk...
and now how he talked....
it wasn't new ..but I don't know why I remembered that time ...when we talked easily
now ..he was talking like a stranger...and as I said....it wasn't new so i had said him why he was talking like that...and he said now I used to talk like that..with everyone...so I remembered whole scenario of my all friends....
.
.
.
Although it was of ...
but At last...I just recalled that time...as a comparison...
but question still remains why everyone one become... different as they grow ...
Why the seriousness ...?
why an unknown expression..?
why different mindset ..?
why multiple ignoring options...?
why different feelings...?
why out of reach...style ..?
why multiple faces .. ?
why..only a beneficial mood...?
why laughing become limited...?
why hangout gets astonished...?
why the fun got vanished..?
why meet ups forget easily??
why loneliness is priority..?
why words carry no physical importance..?
why hurting words become a memory..?
why healing words can't heal...?
why money becomes the only way to get happiness...?
how we become so expertise..?
in a nutshell...!!
people changes as grow...as ...meet others...
Some people says that people lose their emotions as they grow but that's not true
they just simply start to gain importance of emotions to use what ...where....this skill takes a lot of time..
but some people pretend to be matured...and that's where most of friends become stranger
because it's only friendship that has everything in it...Fight advice jokes seriousness it's a combination of all-the friend ship
but when one start to pretend as he is matured... that that bond becomes weak and weaker ..
because when someone pretend to be matured he think himself different than other and then hide everything...from friends and ignore people..
I always think why....why ...why you develop this sense...
.
.
.
At least try to put your self on others mindset try to thinks what it might be look like ...
.
.
.
.
freinds changes...
people changes....
best friend changes...
even your most closed one changes...
so never ever think one might stick to you unless you provide some thing to let the one change....
.
.
.
.
.
.
.
.
.
.
.
.
.
you know what, life goes on....
the one who want to leave you will leave you...
but 
then we strengthen ourselves to be strong...
but again the thoughts ruins the strength...
ruins the ability to adapt the changes...
ruins the calmness that we gain in absence...
ruins the pleasure we gained...by wasting a lot of time...
ruins the smile..we carried at exchange of our emotions...
ruins the life styles we wanted so much to be acquired....
in short ruins the statement of our ongoing life ...
...
so ...
it's not limited to specific people's...
it Carries everything... everyone....every individual....
it makes disturbance as a flavour ...
it makes sadness as a topping...
it makes crying as a structure
it makes greifs as a ladder...
of our life....
I never supported this fact 
"people changes as time passes"
unless I witnessed it myself....
.
.
.
u know what...
one when learn to live like that then taking him back to that time is impossible...
it's a irreversible....`
    ), authors: ['hamza'] },
    createProject(
        'art-beyond', 'Beyond words', 'Silence speaks.',
        <Feather size={50} className="text-yellow-700" />, COLORS.sepia,
        "The value of silence and understanding emotions without the need for words.",
        ['Philosophy', 'Communication', 'Silence'],
        true, null, false, [], 'article',
        `Beyond words
Words don't carry value always. Sometimes who said it ,values more.Ever noticed the evening, gossiping about the life. Blaming the sunset as key factor of sunrise.No right make sense? No...?
Something are meant to be acknowledged. Sometimes letting everything go as it is , works wonders. Not everyone can feels how you can , similarly not everyone can think as u can. Sometimes we need to accept the mistake and move on. Some times we need to cut the story in short and start a new chapter. Not everyone deserves your words, similarly not everyone is capable of accepting and understanding your emotions. The people who are yours will find some way to read the subtitles of your face even when you are silent. Not everytime the explanation is given when we are wrong, sometimes the person in front is very closed , most respected.Not everyone deserves to listen the soothing noice of your tensions.Its nature of humans , to neglect the thing which they get completely free.Sometimes kicking your self allows you to remove each negativity in your own. Facilitating our own mistakes make us agnorant, and ignoring is best way to kill some one. Next time , try to ignore your negative thoughts , problems.
Because unless you go Inside the sea, you can't find the treasure, "sea speaks more honestly to those willing to drown "`
    ),
    { ...createProject(
        'art-affection', 'An innovative affection', 'Complex emotions.',
        <Feather size={50} className="text-yellow-700" />, COLORS.sepia,
        "Navigating the confusion and innovation of affection, attachment, and prioritizing oneself.",
        ['Love', 'Emotions', 'Relationships'],
        true, null, false, [], 'article',
        `An innovative affection
A drastic drama always catches interdisciplinary attention. This attention drags you towards most uncomfortable moments where u can't decide what actually your heart and mind are collaborating. This dramatic game appears when your can't decide whether you are ignoring or your mind keeps on thinking about someone. It's actually an unexplainable feeling because you are happy when they are around you and you become actually sad when they aren't, and you actually don't know why you was happy and why you became sad. You don't talk, interact mostly with them but when you think about their disappearance, you feel yourself in tension. You can't decide whether you are the one who keeps o n thinking about them, or maybe they are thinking about you and then you realize why would they think and then you say why should I think. Now in this wrangle , you sometimes lose your self and sometimes with same energy you get up for next mission of life . In a moment you want to move around them and on very next moment you want to avoid them.With time , maybe u start to think that oh yeah it was just an emotional attachment, but when you deeply analyze you become emotionally unstable. U can't adapt the mesmerizing change in your emotional development, as sometime you take it as no there is nothing and on very next moment it is shaking your stability factor.
With unstable stability you think to make yourself stable and tries to adapt positive changes which helps you to gain peace. In order to get this stability, you underestimate yourself and prioritize them, you make your self a very down to earth person ,a humble one .
You start to ask yourself that question about why is this happening in mind and you are also capable at the same time to answer that questions.And then you find out some unanswerable questions but you ignore as to prioritize them. You learn to prioritize yourself, you learn to prioritize them, you start to actually understand how world goes but you make sure to change the rule . In nutshell, you gain enough understanding of what to do for them and what for yourself, obviously most of time your prioritize them but as the time passes you get feasible knowledge of what to prioritize. Your affection causes a innovation as your story is different from other carrying these incidents.`
    ), authors: ['hamza'] },
    { ...createProject(
        'art-mentor', 'A memorial Mentor', 'Tribute to a teacher.',
        <Feather size={50} className="text-yellow-700" />, COLORS.sepia,
        "A heartfelt tribute to a mentor who taught more than just the subject—teaching how to live.",
        ['Education', 'Mentorship', 'Tribute'],
        true, null, false, [], 'article',
        `A memorial Mentor
.`
    ), authors: ['hamza'] },
];

// --- MAIN MENU ---
const SERVICES = [
    { id: 'web-dev', title: 'Web Apps', desc: 'Full-stack ecosystems. NovaTalk, MindMate & ReqSystem.', icon: <Layers size={50} className="text-blue-400" />, ...COLORS.blue, hasSubMenu: true, subMenuData: WEB_PROJECTS },

    // FYP / RF SENSING (Tagged for ALL)
    { ...createProject(
        'rf-sensing', 'RF Sensing (FYP)', 'Respiratory Complications using IoT.',
        <Radio size={50} className="text-teal-400" />, COLORS.teal,
        "Final Year Project: Developed an AI-powered non-invasive health monitoring system that uses RF (Radio Frequency) sensing to detect and analyze human respiratory patterns without physical contact, capturing real-time signal data through RF hardware, processing it using LabVIEW and IoT infrastructure, and applying intelligent algorithms to classify breathing conditions such as normal, tachypnea, and abnormal patterns, integrated with a cloud-based Firebase backend enabling real-time monitoring, historical data tracking, and remote access for patients and healthcare providers, while incorporating an AI-driven mechanism that automatically detects anomalies and triggers safety actions including alarms, smart lighting, and GPS-based alerts/notifications through IoT control systems, designed as a scalable solution for smart healthcare environments demonstrating the fusion of RF sensing, AI analytics, and IoT for continuous, contactless health monitoring. ##",
        ['IoT', 'USRP & LabVIEW', 'Raspberry Pi', 'AI/ML Model Training', 'React' , 'Firebase' ,'LLM'],
        false, '/fyp', true,
        []
    ), authors: ['hamza', 'fahad', 'asad'] },
     { ...createProject(
        'ats-resume', 'ATS Resume Builder', 'AI-driven resume optimization ecosystem.',
        <FileText size={50} className="text-blue-400" />, COLORS.blue,
        "A sophisticated career-tech platform designed to bridge the gap between candidates and Automated Tracking Systems (ATS). Users can upload PDFs or paste raw experience, which is then processed by a custom n8n workflow and Groq-powered AI. \n\nThe system analyzes job descriptions to inject high-impact keywords, scores the resume's hiring strength, and generates a perfectly formatted, ATS-compliant PDF. It features a credit-based system, real-time AI scoring, and a modern 'glassmorphism' UI.",
        ['React', 'Firebase', 'n8n Automation', 'LLM', 'Stripe' ],
        true, 'https://ats-resume-build.web.app', false,
        ['/assets/projects/ats1.jpeg']
    ), authors: ['hamza'] },

    { id: 'ai-agents', title: 'AI & Models', desc: 'Computer Vision, Threat Intelligence & LLMs.', icon: <Brain size={50} className="text-rose-400" />, ...COLORS.rose, hasSubMenu: true, subMenuData: AI_PROJECTS },
    { id: 'embedded', title: 'Embedded', desc: 'PCB Design, ESP32 Gaming, & Control Systems.', icon: <Cpu size={50} className="text-amber-400" />, ...COLORS.amber, hasSubMenu: true, subMenuData: EMBEDDED_PROJECTS },
    { id: 'robotics', title: 'Robotics', desc: 'Sumo Robots (Winners) & Line Followers.', icon: <Bot size={50} className="text-orange-500" />, ...COLORS.orange, hasSubMenu: true, subMenuData: ROBOTICS_PROJECTS },
    { id: 'content', title: 'Content Writing', desc: 'Technical articles & creative thoughts.', icon: <PenTool size={50} className="text-yellow-600" />, ...COLORS.sepia, hasSubMenu: true, subMenuData: CONTENT_PROJECTS },
    { id: 'rust-3d', title: 'Rust & 3D', desc: 'Dental segmentation, Simulation & Engraving.', icon: <Box size={50} className="text-orange-400" />, ...COLORS.rust, hasSubMenu: true, subMenuData: RUST_PROJECTS },
    { id: 'data-cpp', title: 'Data & Algo', desc: 'Database Arch, C++ optimization & Algorithms.', icon: <Database size={50} className="text-slate-400" />, ...COLORS.slate, hasSubMenu: true, subMenuData: DATA_PROJECTS }
    
];


// ==========================================
// === 2. REALISTIC BOOK READER COMPONENT ===
// ==========================================
const BookReader = ({ project, onClose }) => {
    const [spreadIndex, setSpreadIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const [isFlipping, setIsFlipping] = useState(false);
    const [showContent, setShowContent] = useState(true);
    
    // NEW: Listen for mobile to adjust pagination rules dynamically
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const paginateText = (fullText) => {
        if (!fullText) return [];
        
        if (fullText.includes("<<<PAGE>>>")) {
            return fullText.split("<<<PAGE>>>").map(p => ({ type: 'text', content: p.trim() }));
        }

        // DYNAMIC RESPONSIVE PAGINATION VARIABLES
        const CHARS_PER_LINE = isMobile ? 28 : 41; 
        const LINES_PER_PAGE = isMobile ? 10 : 12; 
        
        const pages = [];
        const sections = fullText.split(/(<<<IMG:.*?>>>)/g);

        sections.forEach(section => {
            section = section.trim();
            if (!section) return;

            if (section.startsWith("<<<IMG:") && section.endsWith(">>>")) {
                pages.push({ type: 'image', url: section.slice(7, -3) });
            } else {
                let currentLine = "";
                let currentLinesOnPage = 0;
                let currentPageText = "";
                
                const paragraphs = section.split('\n');
                
                paragraphs.forEach((para) => {
                    const trimmedPara = para.trim();
                    if (!trimmedPara) {
                        currentLinesOnPage++;
                        currentPageText += "\n";
                        if (currentLinesOnPage >= LINES_PER_PAGE) {
                            pages.push({ type: 'text', content: currentPageText.trim() });
                            currentPageText = "";
                            currentLinesOnPage = 0;
                        }
                        return;
                    }
                    
                    const words = trimmedPara.split(' ');
                    words.forEach((word) => {
                        if ((currentLine.length + word.length + 1) > CHARS_PER_LINE) {
                            currentPageText += currentLine + "\n";
                            currentLinesOnPage++;
                            currentLine = word + " ";
                            if (currentLinesOnPage >= LINES_PER_PAGE) {
                                pages.push({ type: 'text', content: currentPageText.trim() });
                                currentPageText = "";
                                currentLinesOnPage = 0;
                            }
                        } else {
                            currentLine += word + " ";
                        }
                    });
                    if (currentLine.length > 0) {
                        currentPageText += currentLine + "\n";
                        currentLinesOnPage++;
                        currentLine = "";
                        if (currentLinesOnPage >= LINES_PER_PAGE) {
                            pages.push({ type: 'text', content: currentPageText.trim() });
                            currentPageText = "";
                            currentLinesOnPage = 0;
                        }
                    }
                });
                if (currentPageText.trim().length > 0) {
                    pages.push({ type: 'text', content: currentPageText.trim() });
                }
            }
        });

        return pages;
    };

    // --- PAGE CONSTRUCTION (FIXED MAPPING) ---
    const allPages = useMemo(() => {
        if (!project) return [];

        const titlePage = { type: 'title', title: project.title, author: "Muhammad Hamza" };
        
        // FIX: Don't overwrite the 'image' type. Check what paginateText returned.
        const contentPages = paginateText(project.fullArticle).map(page => {
            // If it's already an object (image or text), keep it. If string, wrap it.
            if (typeof page === 'object') return page; 
            return { type: 'text', content: page };
        });
        
        const reviewPage = { type: 'review', projectTitle: project.title };
        const endPage = { type: 'end' };

        const pages = [titlePage, ...contentPages, reviewPage, endPage];

        if (pages.length % 2 !== 0) {
            pages.push({ type: 'empty' });
        }
        return pages;
    }, [project]);

    if (!project) return null;

    // --- NAVIGATION LOGIC ---
    const handleNext = () => {
        if (isFlipping || spreadIndex + 2 >= allPages.length) return;
        setDirection(1);
        setIsFlipping(true);
        setShowContent(false);
    };

    const handlePrev = () => {
        if (isFlipping || spreadIndex - 2 < 0) return;
        setDirection(-1);
        setIsFlipping(true);
        setShowContent(false);
    };

    const handleAnimationComplete = () => {
        if (direction === 1) {
            setSpreadIndex((prev) => prev + 2);
        } else if (direction === -1) {
            setSpreadIndex((prev) => prev - 2);
        }
        setIsFlipping(false);
        setDirection(0);
        setTimeout(() => setShowContent(true), 100);
    };

    // --- RENDER HELPERS ---
    const renderPageContent = (pageData, index, isContentVisible = true) => {
        if (!pageData || pageData.type === 'empty') return <div className="w-full h-full"></div>;
        const contentClass = `w-full h-full transition-opacity duration-700 ${isContentVisible ? 'opacity-100' : 'opacity-0'}`;

        // 1. Title Page
        if (pageData.type === 'title') {
            return (
                <div className={contentClass}>
                    <div className="flex flex-col h-full items-center justify-center text-center p-8 border-4 border-double border-[#8b5a2b]/20 m-4">
                        <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#3e2723] mb-4 tracking-wide">{pageData.title}</h1>
                        <div className="w-16 h-1 bg-[#8b5a2b] mb-4"></div>
                        <p className="text-lg font-serif italic text-[#5d4037]">Written by</p>
                        <p className="text-xl font-bold uppercase tracking-widest text-[#3e2723] mt-2">{pageData.author}</p>
                        <div className="mt-12 opacity-50"><Feather size={24} className="text-[#8b5a2b]" /></div>
                    </div>
                </div>
            );
        }

        // 2. Review Page
        if (pageData.type === 'review') {
            return (
                <div className={contentClass}>
                     <div className="flex flex-col h-full p-4 relative">
                        <h3 className="text-xl font-serif font-bold text-[#3e2723] mb-6 text-center border-b border-[#8b5a2b]/20 pb-4">
                            Reader's Thoughts
                        </h3>
                        <form 
                            action="https://formspree.io/f/xdalgpgb" 
                            method="POST" 
                            className="flex flex-col gap-4 flex-grow"
                            target="_blank"
                        >
                            <input type="hidden" name="Article Title" value={pageData.projectTitle} />
                            <div className="flex flex-col">
                                <label className="text-xs font-bold uppercase tracking-widest text-[#8b5a2b] mb-1">Your Name</label>
                                <input type="text" name="name" placeholder="Traveler..." className="bg-transparent border-b-2 border-[#8b5a2b]/30 p-2 text-[#3e2723] font-serif placeholder-[#8b5a2b]/40 focus:outline-none focus:border-[#8b5a2b] transition-colors" required />
                            </div>
                            <div className="flex flex-col flex-grow">
                                <label className="text-xs font-bold uppercase tracking-widest text-[#8b5a2b] mb-1">Your Review</label>
                                <textarea name="message" placeholder="Write your thoughts here..." className="bg-[#3e2723]/5 w-full h-full min-h-[150px] p-3 text-[#3e2723] font-serif rounded-lg resize-none border border-transparent focus:border-[#8b5a2b]/30 focus:outline-none focus:bg-[#3e2723]/10 transition-colors" required></textarea>
                            </div>
                            <button type="submit" className="mt-auto py-3 bg-[#3e2723] text-[#f4e4bc] font-bold uppercase tracking-widest text-xs rounded shadow-md hover:bg-[#5d4037] transition-all flex items-center justify-center gap-2">
                                Send to Author <Send size={14} />
                            </button>
                        </form>
                    </div>
                </div>
            );
        }

        // 3. Image Page (New)
        // 3. Image Page (New)
        if (pageData.type === 'image') {
            return (
                <div className={contentClass}>
                    <div className="w-full h-full flex flex-col items-center justify-center py-4">
                         <div className="w-full relative border-4 border-[#3e2723]/20 p-2 bg-white shadow-inner flex-grow flex items-center justify-center overflow-hidden">
                            {/* Native lazy loading added */}
                            <img 
                                src={pageData.url} 
                                alt="Article illustration" 
                                loading="lazy"
                                className="max-w-full max-h-[100%] object-contain sepia-[.3] contrast-125"
                            />
                         </div>
                         <p className="mt-4 text-xs font-serif italic text-[#8b5a2b] shrink-0">Fig. {index}</p>
                    </div>
                </div>
            );
        }

        // 4. End Page
        if (pageData.type === 'end') {
            return (
                <div className={contentClass}>
                    <div className="flex flex-col h-full items-center justify-center text-center p-8">
                        <h1 className="text-2xl font-serif font-bold text-[#3e2723] mb-4 tracking-widest">The End</h1>
                        <div className="w-8 h-1 bg-[#8b5a2b] mb-4"></div>
                        <div className="opacity-50"><Feather size={16} className="text-[#8b5a2b]" /></div>
                    </div>
                </div>
            );
        }

        // 5. Content Page (Text)
        return (
            <div className={`flex flex-col h-full ${contentClass}`}>
                <h3 className="text-xs font-bold uppercase tracking-widest text-[#8b5a2b] mb-4 border-b border-[#8b5a2b]/20 pb-2 flex justify-between">
                    <span>{project.title}</span>
                    <span>Page {index}</span>
                </h3>
                <div className="font-serif italic text-lg leading-loose whitespace-pre-wrap text-[#2c1810] flex-grow drop-shadow-sm overflow-hidden text-justify pb-4">
                    {pageData.content || pageData.text} 
                </div>
                <div className="mt-auto text-center text-[#8b5a2b]/40 text-xs font-serif pt-2 border-t border-[#8b5a2b]/10">- {index} -</div>
            </div>
        );
    };

    // Animation Logic Vars
    let staticLeftIndex, staticRightIndex, flipperFrontIndex, flipperBackIndex;
    let flipRotationStart = 0; let flipRotationEnd = 0;
    let flipperFrontContent = null;
    let flipperBackContent = null;

    if (!isFlipping) {
        staticLeftIndex = spreadIndex;
        staticRightIndex = spreadIndex + 1;
    } else {
        if (direction === 1) { 
            staticLeftIndex = spreadIndex + 2;
            staticRightIndex = spreadIndex + 3;
            flipperFrontIndex = spreadIndex + 1;
            flipperBackIndex = spreadIndex + 2;
            flipRotationStart = 0; flipRotationEnd = -180;
            flipperFrontContent = renderPageContent(allPages[flipperFrontIndex], flipperFrontIndex, true);
            flipperBackContent = renderPageContent(allPages[flipperBackIndex], flipperBackIndex, true);
        } else { 
            staticLeftIndex = spreadIndex - 2;
            staticRightIndex = spreadIndex - 1;
            flipperFrontIndex = spreadIndex - 1;
            flipperBackIndex = spreadIndex;
            flipRotationStart = -180; flipRotationEnd = 0;
            flipperFrontContent = renderPageContent(allPages[flipperFrontIndex], flipperFrontIndex, true);
            flipperBackContent = renderPageContent(allPages[flipperBackIndex], flipperBackIndex, true);
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-[200] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4"
        >
            <button onClick={onClose} className="absolute top-6 right-6 z-50 text-white/50 hover:text-white transition-colors">
                <X size={32} />
            </button>

            {/* === DESKTOP BOOK === */}
            <div className="hidden md:flex relative perspective-2000">
                <div className="relative bg-[#3e2723] p-4 rounded-xl shadow-2xl border-4 border-[#2c1810] flex gap-0">
                    <div className="absolute top-0 bottom-0 left-1/2 w-12 -ml-6 bg-gradient-to-r from-transparent via-black/20 to-transparent z-20 pointer-events-none rounded-sm"></div>

                    {/* Static Left */}
                    <div className="w-[450px] h-[650px] bg-[#f4e4bc] rounded-l-md border-r border-[#d7c9a5] overflow-hidden shadow-inner relative">
                        <div className="absolute inset-0 pointer-events-none opacity-20 mix-blend-multiply bg-[url('https://grainy-gradients.vercel.app/noise.svg')]"></div>
                        <div className="h-full w-full p-10">
                            {staticLeftIndex >= 0 && staticLeftIndex < allPages.length &&
                                renderPageContent(allPages[staticLeftIndex], staticLeftIndex, isFlipping ? false : showContent)}
                        </div>
                    </div>

                    {/* Static Right */}
                    <div className="w-[450px] h-[650px] bg-[#f4e4bc] rounded-r-md border-l border-[#d7c9a5] overflow-hidden shadow-inner relative">
                        <div className="absolute inset-0 pointer-events-none opacity-20 mix-blend-multiply bg-[url('https://grainy-gradients.vercel.app/noise.svg')]"></div>
                        <div className="h-full w-full p-10">
                            {staticRightIndex >= 0 && staticRightIndex < allPages.length &&
                                renderPageContent(allPages[staticRightIndex], staticRightIndex, isFlipping ? false : showContent)}
                        </div>
                    </div>

                    {/* Flipper */}
                    <AnimatePresence onExitComplete={() => { }}>
                        {isFlipping && (
                            <motion.div
                                initial={{ rotateY: flipRotationStart }}
                                animate={{ rotateY: flipRotationEnd }}
                                transition={{ duration: 0.8, ease: "easeInOut" }}
                                onAnimationComplete={handleAnimationComplete}
                                className="absolute top-4 bottom-4 right-4 w-[450px] h-[650px]"
                                style={{ transformStyle: 'preserve-3d', transformOrigin: 'left', zIndex: 50 }}
                            >
                                <div className="absolute inset-0 bg-[#f4e4bc] rounded-r-md border-l border-[#d7c9a5] overflow-hidden p-10 backface-hidden">
                                    <div className="absolute inset-0 pointer-events-none opacity-20 mix-blend-multiply bg-[url('https://grainy-gradients.vercel.app/noise.svg')]"></div>
                                    <div className="absolute inset-0 bg-gradient-to-r from-black/5 to-transparent pointer-events-none"></div>
                                    {flipperFrontContent}
                                </div>
                                <div className="absolute inset-0 bg-[#f4e4bc] rounded-l-md border-r border-[#d7c9a5] overflow-hidden p-10 backface-hidden" style={{ transform: "rotateY(180deg)" }}>
                                    <div className="absolute inset-0 pointer-events-none opacity-20 mix-blend-multiply bg-[url('https://grainy-gradients.vercel.app/noise.svg')]"></div>
                                    <div className="absolute inset-0 bg-gradient-to-l from-black/5 to-transparent pointer-events-none"></div>
                                    {flipperBackContent}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* === MOBILE VIEW === */}
            <div className="md:hidden w-full h-[80vh] bg-[#f4e4bc] rounded-lg relative overflow-hidden shadow-2xl flex flex-col m-4 border-2 border-[#3e2723]">
                <div className="absolute inset-0 pointer-events-none opacity-20 mix-blend-multiply bg-[url('https://grainy-gradients.vercel.app/noise.svg')]"></div>
                <div className="flex-grow overflow-y-auto custom-scrollbar-sepia p-6">
                    {renderPageContent(allPages[spreadIndex], spreadIndex, true)}
                </div>
                <div className="p-4 bg-[#e6d5aa] flex justify-between items-center text-[#8b5a2b] border-t border-[#d7c9a5]">
                    <button disabled={spreadIndex === 0} onClick={() => setSpreadIndex(p => p - 1)} className="p-2 disabled:opacity-30"><ChevronLeft /></button>
                    <span className="text-xs font-serif">Page {spreadIndex} / {allPages.length - 1}</span>
                    <button disabled={spreadIndex >= allPages.length - 1} onClick={() => setSpreadIndex(p => p + 1)} className="p-2 disabled:opacity-30"><ChevronRight /></button>
                </div>
            </div>

            {/* Controls (Desktop) */}
            <div className="hidden md:block">
                <button onClick={handlePrev} disabled={spreadIndex === 0 || isFlipping} className={`absolute top-1/2 left-10 transform -translate-y-1/2 p-4 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all ${spreadIndex === 0 || isFlipping ? 'opacity-20 cursor-not-allowed' : 'opacity-100 hover:scale-110'}`}><ChevronLeft size={32} /></button>
                <button onClick={handleNext} disabled={spreadIndex >= allPages.length - 2 || isFlipping} className={`absolute top-1/2 right-10 transform -translate-y-1/2 p-4 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all ${spreadIndex >= allPages.length - 2 || isFlipping ? 'opacity-20 cursor-not-allowed' : 'opacity-100 hover:scale-110'}`}><ChevronRight size={32} /></button>
            </div>

            <style>{`
                .perspective-2000 { perspective: 2500px; } 
                .backface-hidden { backface-visibility: hidden; }
                .custom-scrollbar-sepia::-webkit-scrollbar { width: 4px; } 
                .custom-scrollbar-sepia::-webkit-scrollbar-track { background: rgba(139, 90, 43, 0.05); } 
                .custom-scrollbar-sepia::-webkit-scrollbar-thumb { background: rgba(139, 90, 43, 0.3); border-radius: 10px; }
            `}</style>
        </motion.div>
    );
};

// ==========================================
// === 3. MODAL & OVERLAY COMPONENTS (WITH ANIMATED SKELETON GALLERY) ===
// ==========================================

const ProjectModal = ({ project, onClose, onLaunchBook }) => {
    const navigate = useNavigate();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const [isImageLoading, setIsImageLoading] = useState(true);

    if (!project) return null;

    const handleLaunch = () => {
        if (project.type === 'article') {
            onLaunchBook(); // Trigger Book Reader
            return;
        }

        if (project.isInternalRoute) {
            navigate(project.link);
        } else {
            window.open(project.link, '_blank');
        }
        onClose();
    };

    const hasImages = project.images && project.images.length > 0;

    const nextImage = (e) => {
        e.stopPropagation();
        setDirection(1);
        setIsImageLoading(true);
        setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
    };

    const prevImage = (e) => {
        e.stopPropagation();
        setDirection(-1);
        setIsImageLoading(true);
        setCurrentImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
    };

    const slideVariants = {
        enter: (direction) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
        center: { zIndex: 1, x: 0, opacity: 1 },
        exit: (direction) => ({ zIndex: 0, x: direction < 0 ? 300 : -300, opacity: 0 })
    };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-[150] bg-black/90 backdrop-blur-[20px] flex items-center justify-center p-4 md:p-8"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
                className="bg-[#0a0a0a] border border-white/10 rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col md:flex-row relative shadow-2xl"
                style={{ boxShadow: `0 0 100px ${project.hex}20` }}
                onClick={(e) => e.stopPropagation()}
            >
                <button onClick={onClose} className="absolute top-4 right-4 z-50 bg-black/50 p-2 rounded-full text-white/70 hover:text-white transition-colors">
                    <X size={24} />
                </button>

                {/* Left Column: Info */}
                <div className="p-8 md:w-1/2 flex flex-col h-full overflow-y-auto custom-scrollbar order-2 md:order-1">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-16 h-16 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shadow-lg shrink-0">
                            {project.icon}
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-white leading-tight">{project.title}</h2>
                            <div className="flex items-center gap-2 mt-2">
                                {project.type === 'article' ? (
                                    <span className="text-xs font-bold uppercase tracking-wider text-yellow-500 flex items-center gap-1"><BookOpen size={12} /> Article / Content</span>
                                ) : (project.isOnlineStatus ? (
                                    <span className="text-xs font-bold uppercase tracking-wider text-green-400 flex items-center gap-1"><Wifi size={12} /> Live / Online</span>
                                ) : (
                                    <span className="text-xs font-bold uppercase tracking-wider text-orange-400 flex items-center gap-1"><WifiOff size={12} /> Local / Internal</span>
                                ))}
                            </div>
                        </div>
                    </div>

                    <p className="text-gray-300 leading-relaxed mb-8 flex-grow whitespace-pre-line text-sm md:text-base">
                        {project.longDesc || project.desc}
                    </p>

                    <div className="mb-8 shrink-0">
                        <h4 className="text-xs uppercase tracking-wider text-white/50 font-bold mb-3">Technology & Topics</h4>
                        <div className="flex flex-wrap gap-2">
                            {project.techStack && project.techStack.map((tech, i) => (
                                <span key={i} className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-white/80">{tech}</span>
                            ))}
                        </div>
                    </div>

                    <div className="mt-auto shrink-0">
                        {/* Custom Button for Articles */}
                        {project.type === 'article' ? (
                            <button onClick={handleLaunch} className="w-full py-4 rounded-xl font-bold text-black text-sm uppercase tracking-wider flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform shadow-xl" style={{ background: `linear-gradient(135deg, ${project.glowHex}, ${project.hex})` }}>
                                Read Article <BookOpen size={18} />
                            </button>
                        ) : (
                            project.link ? (
                                <button onClick={handleLaunch} className="w-full py-4 rounded-xl font-bold text-black text-sm uppercase tracking-wider flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform shadow-xl" style={{ background: `linear-gradient(135deg, ${project.glowHex}, ${project.hex})` }}>
                                    Launch / View <ExternalLink size={18} />
                                </button>
                            ) : (
                                <button disabled className="w-full py-4 rounded-xl font-bold text-white/50 text-sm uppercase tracking-wider flex items-center justify-center gap-2 bg-white/5 border border-white/10 cursor-not-allowed">
                                    No Launchable Interface <ShieldCheck size={18} />
                                </button>
                            )
                        )}
                    </div>
                </div>

                {/* Right Column: Gallery (Updated with SKELETON LOADER) */}
                <div className="md:w-1/2 bg-black/50 relative flex items-center justify-center border-l border-white/10 order-1 md:order-2 min-h-[300px] md:min-h-full overflow-hidden">
                    {hasImages ? (
                        <div className="relative w-full h-full flex items-center justify-center group">

                            {/* NEW: SKELETON LOADER */}
                            {isImageLoading && (
                                <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/5 animate-pulse">
                                    <div className="flex flex-col items-center opacity-30">
                                        <ImageIcon size={48} className="mb-2" />
                                        <span className="text-xs uppercase tracking-widest font-bold">Loading Preview...</span>
                                    </div>
                                </div>
                            )}

                            {/* Animated Image Container */}
                            <AnimatePresence initial={false} custom={direction}>
                                <motion.img
                                    key={currentImageIndex}
                                    src={project.images[currentImageIndex]}
                                    alt={`${project.title} Preview ${currentImageIndex + 1}`}
                                    custom={direction}
                                    variants={slideVariants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    onLoad={() => setIsImageLoading(false)}
                                    transition={{
                                        x: { type: "spring", stiffness: 300, damping: 30 },
                                        opacity: { duration: 0.2 }
                                    }}
                                    className={`absolute w-full h-full object-contain bg-black/20 backdrop-blur-sm transition-opacity duration-300 ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        setIsImageLoading(false);
                                    }}
                                />
                            </AnimatePresence>

                            {/* Navigation Arrows */}
                            {project.images.length > 1 && (
                                <>
                                    <button onClick={prevImage} className="absolute left-4 p-2 rounded-full bg-black/50 text-white/70 hover:text-white hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100 z-30">
                                        <ChevronLeft size={24} />
                                    </button>
                                    <button onClick={nextImage} className="absolute right-4 p-2 rounded-full bg-black/50 text-white/70 hover:text-white hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100 z-30">
                                        <ChevronRight size={24} />
                                    </button>
                                    <div className="absolute bottom-4 flex gap-2 z-30">
                                        {project.images.map((_, idx) => (
                                            <div key={idx} className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIndex ? 'bg-white scale-110' : 'bg-white/30'}`} />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        // Fallback / Coming Soon
                        <div className="absolute inset-0 bg-gradient-to-br from-black/80 to-transparent z-0 flex items-center justify-center">
                            <div className="text-center p-8 relative z-10 opacity-50">
                                {project.type === 'article' ? (
                                    <>
                                        <BookOpen size={64} className="mx-auto mb-4 text-white/30" />
                                        <p className="text-white/50 text-sm uppercase tracking-widest font-bold">Reader Mode</p>
                                        <p className="text-white/30 text-xs mt-2">Click "Read Article" to open the book.</p>
                                    </>
                                ) : (
                                    <>
                                        <ImageIcon size={64} className="mx-auto mb-4 text-white/30" />
                                        <p className="text-white/50 text-sm uppercase tracking-widest font-bold">Project Gallery</p>
                                        <p className="text-white/30 text-xs mt-2">Images Coming Soon.<br />(Or add to 'public/assets')</p>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

// Updated Member Photo Component with Skeleton
const MemberPhoto = ({ src, alt, icon }) => {
    const [isLoaded, setIsLoaded] = useState(false);

    return (
        <div className="w-24 h-24 rounded-full bg-white/5 mx-auto mb-4 overflow-hidden relative group border border-white/10">
            {/* Skeleton */}
            {!isLoaded && (
                <div className="absolute inset-0 bg-white/10 animate-pulse z-10" />
            )}

            {/* Image */}
            <img
                src={src}
                alt={alt}
                onLoad={() => setIsLoaded(true)}
                className={`w-full h-full object-cover relative z-0 transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                onError={(e) => { e.target.style.display = 'none'; setIsLoaded(true); }} // Fallback to icon
            />

            {/* Icon Fallback (Shows if image fails or while loading behind) */}
            <div className={`absolute inset-0 flex items-center justify-center z-[-1] ${isLoaded ? 'opacity-0' : 'opacity-100'}`}>
                {icon}
            </div>
        </div>
    );
};

// Updated About Overlay
const AboutOverlay = ({ onClose }) => (
    <motion.div
        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="absolute inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-6 md:p-12 overflow-y-auto custom-scrollbar"
    >
        <button onClick={onClose} className="absolute top-8 right-8 text-white/50 hover:text-white transition-colors"><X size={32} /></button>
        <div className="max-w-6xl w-full text-white mt-16 md:mt-0">
            <h2 className="text-4xl md:text-6xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 text-center">Synapse Trio.</h2>
            <h3 className="text-xl md:text-2xl font-light mb-8 text-white/70 italic text-center">Turning Thoughts into Actions.</h3>

            <div className="max-w-4xl mx-auto text-center mb-12 text-gray-300 leading-relaxed">
                <p className="mb-4">We are three Computer Engineering students from COMSATS University Islamabad, united by a passion for delivering perfect technological solutions. As a collective, we stack our diverse expertise to tackle complex challenges.</p>
                <p>Our experience includes a significant 6-month internship with a French technology firm, specializing in high-performance Rust development for 3D modeling and dental applications. From winning national robotics competitions like 'Zero RoboKai' to developing cutting-edge web and embedded systems, we bridge the gap between hardware and software.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
                {/* Muhammad Hamza */}
                <div className="bg-white/5 p-6 rounded-2xl border border-white/10 hover:border-blue-500/50 transition-colors flex flex-col">
                    <MemberPhoto src="/assets/members/hamza.jpg" alt="Muhammad Hamza" icon={<User size={40} className="text-blue-400/50" />} />
                    <h3 className="text-2xl font-bold mb-2 text-center">Muhammad Hamza</h3>
                    <p className="text-blue-400 text-sm font-bold uppercase tracking-wider mb-4 text-center">Web Dev, AI & 3D Modeling</p>
                    <p className="text-gray-300 text-sm leading-relaxed flex-grow">Passionate about full-stack web development and AI integration. Experienced in complex 3D tasks including occlusion plane creation with manipulators, text engraving on models, and simulating mandible rotations for TMJ disorder analysis.</p>
                    <div className="flex gap-4 justify-center mt-6">
                        <a href="https://github.com/Muhammad-Hamza-123" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-blue-400 transition-colors"><Github size={20} /></a>
                        {/* Direct Gmail Link */}
                        <a href="https://mail.google.com/mail/?view=cm&fs=1&to=muhammadhamza1q@gmail.com" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-blue-400 transition-colors"><Mail size={20} /></a>
                    </div>
                </div>
                
                {/* Muhammad Fahad */}
                <div className="bg-white/5 p-6 rounded-2xl border border-white/10 hover:border-amber-500/50 transition-colors flex flex-col">
                    <MemberPhoto src="/assets/members/fahad.jpg" alt="Muhammad Fahad" icon={<Cpu size={40} className="text-amber-400/50" />} />
                    <h3 className="text-2xl font-bold mb-2 text-center">Muhammad Fahad</h3>
                    <p className="text-amber-400 text-sm font-bold uppercase tracking-wider mb-4 text-center">Architecture, PC Apps & 3D</p>
                    <p className="text-gray-300 text-sm leading-relaxed flex-grow">Specializes in excellent architectural design for systems like robotics structures and ramps. Develops PC applications (e.g., IDM clones) and handles 3D tasks such as hook detection, collision analysis, and special pad design.</p>
                    <div className="flex gap-4 justify-center mt-6">
                        <a href="https://github.com/fahads-github-username" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-amber-400 transition-colors"><Github size={20} /></a>
                        {/* Direct Gmail Link */}
                        <a href="https://mail.google.com/mail/?view=cm&fs=1&to=fahad@example.com" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-amber-400 transition-colors"><Mail size={20} /></a>
                    </div>
                </div>
                
                {/* Muhammad Asad */}
                <div className="bg-white/5 p-6 rounded-2xl border border-white/10 hover:border-orange-500/50 transition-colors flex flex-col">
                    <MemberPhoto src="/assets/members/asad.jpg" alt="Muhammad Asad" icon={<Box size={40} className="text-orange-400/50" />} />
                    <h3 className="text-2xl font-bold mb-2 text-center">Muhammad Asad</h3>
                    <p className="text-orange-400 text-sm font-bold uppercase tracking-wider mb-4 text-center">Hardware, Embedded & 3D</p>
                    <p className="text-gray-300 text-sm leading-relaxed flex-grow">The hardware and embedded systems expert, proficient with microcontrollers. In 3D modeling, he focuses on precise segmentation tasks, special mesh repairs, and bracket designing.</p>
                    <div className="flex gap-4 justify-center mt-6">
                        <a href="https://github.com/Asadkhokhar185" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-orange-400 transition-colors"><Github size={20} /></a>
                        {/* Direct Gmail Link */}
                        <a href="https://mail.google.com/mail/?view=cm&fs=1&to=asadkhokhar185@gmail.com" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-orange-400 transition-colors"><Mail size={20} /></a>
                    </div>
                </div>
            </div>
        </div>
    </motion.div>
);

const ContactOverlay = ({ onClose }) => {
    const [status, setStatus] = useState('idle');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('sending');
alert("Contact form is currently off please use email instead.");
        // 1. Gather the form data
        const formData = {
            
            name: e.target.name.value,
            email: e.target.email.value,
            message: e.target.message.value
        };

        try {
            // 2. Replace with your actual Make.com Custom Webhook URL
            // const webhookUrl = '1https://hook.eu1.make.com/wsady7gg7329qi9mtg8f1nd74x2qiktg';
            // alert("Contact form is currently off please use email instead.");
            
            // // 3. Send the data to Make via POST request
            // const response = await fetch(webhookUrl, {
            //     method: 'POST',
            //     headers: {
            //         'Content-Type': 'application/json',
            //     },
            //     body: JSON.stringify(formData)
            // });

            // if (response.ok) {
            //     setStatus('success');
            //     e.target.reset();
            //     setTimeout(() => onClose(), 2000); // Auto-close modal after success
            // } else {
            //     setStatus('error');
            // }
        } catch (error) {
            console.error('Error sending message:', error);
            alert("Contact form is currently off please use email instead.");
            setStatus('error');
        }
    };

    return (
        <motion.div
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="absolute inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-6"
        >
            <button onClick={onClose} className="absolute top-8 right-8 text-white/50 hover:text-white transition-colors">
                <X size={32} />
            </button>
            <div className="max-w-md w-full bg-white/5 border border-white/10 rounded-3xl p-8 md:p-12 relative overflow-hidden text-center shadow-2xl">
                <div className="absolute top-0 right-0 -mt-16 -mr-16 text-white/5"><Send size={150} /></div>
                
                <h2 className="text-3xl font-bold text-white mb-2 relative z-10">Let's Connect</h2>
                <p className="text-gray-400 mb-8 relative z-10">Send a message and our AI agent will reply instantly.</p>
                
                {/* 4. The Form UI */}
                <form onSubmit={handleSubmit} className="flex flex-col gap-4 relative z-10 text-left">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold uppercase tracking-widest text-white/50">Your Name</label>
                        <input type="text" name="name" required className="bg-white/5 border border-white/10 p-3 rounded-lg text-white focus:border-blue-400 focus:outline-none transition-colors" placeholder="John Doe" />
                    </div>
                    
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold uppercase tracking-widest text-white/50">Your Email</label>
                        <input type="email" name="email" required className="bg-white/5 border border-white/10 p-3 rounded-lg text-white focus:border-blue-400 focus:outline-none transition-colors" placeholder="john@example.com" />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold uppercase tracking-widest text-white/50">Message</label>
                        <textarea name="message" required rows="4" className="bg-white/5 border border-white/10 p-3 rounded-lg text-white focus:border-blue-400 focus:outline-none transition-colors resize-none" placeholder="How can we help?"></textarea>
                    </div>

                    <button 
                        type="submit" 
                        disabled={status === 'sending' || status === 'success'}
                        className={`w-full py-4 mt-2 rounded-xl text-white font-bold uppercase tracking-wider shadow-lg transition-transform flex items-center justify-center gap-2 ${status === 'success' ? 'bg-green-500' : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:scale-[1.02]'}`}
                    >
                        {status === 'idle' && <>Send Message <Send size={18} /></>}
                        {status === 'sending' && 'Sending...'}
                        {status === 'success' && 'Message Sent!'}
                        {status === 'error' && 'Failed. Try Again.'}
                    </button>
                </form>
            </div>
        </motion.div>
    );
};

// ==========================================
// === 4. MAIN PORTFOLIO RENDER (Updated Main Logo with Skeleton) ===
// ==========================================
// ==========================================
// === INTERACTIVE CANVAS PARTICLE BACKGROUND ===
// ==========================================
const ParticleBackground = ({ activeColor }) => {
    const canvasRef = React.useRef(null);
    const particlesRef = React.useRef([]); // Keeps track of particles so we can update their colors live

    React.useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animationFrameId;
        
        const particleCount = window.innerWidth < 768 ? 80 : 250; 
        
        let mouse = { x: null, y: null, radius: 150 };

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        window.addEventListener('resize', resizeCanvas);
        resizeCanvas();

        window.addEventListener('mousemove', (event) => {
            mouse.x = event.x;
            mouse.y = event.y;
        });

        window.addEventListener('mouseout', () => {
            mouse.x = null;
            mouse.y = null;
        });

        class Particle {
            constructor() {
                this.x = Math.random() * canvas.width;
                this.y = Math.random() * canvas.height;
                this.size = Math.random() * 2 + 0.5;
                this.baseX = this.x;
                this.baseY = this.y;
                this.density = (Math.random() * 30) + 1;
                
                // 50% chance to be crisp white, 50% chance to be the dynamic theme color
                this.isThemeColor = Math.random() > 0.5;
                this.color = this.isThemeColor ? activeColor : '#ffffff';
                this.opacity = Math.random() * 0.5 + 0.2;
            }

            draw() {
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.closePath();
                ctx.fillStyle = this.color;
                ctx.globalAlpha = this.opacity;
                ctx.fill();
            }

            update() {
                this.baseY -= 0.5;
                if (this.baseY < -10) {
                    this.baseY = canvas.height + 10;
                    this.baseX = Math.random() * canvas.width;
                    this.x = this.baseX;
                }

                let dx = mouse.x - this.x;
                let dy = mouse.y - this.y;
                let distance = Math.sqrt(dx * dx + dy * dy);
                let forceDirectionX = dx / distance;
                let forceDirectionY = dy / distance;
                let force = (mouse.radius - distance) / mouse.radius;

                if (distance < mouse.radius) {
                    this.x -= forceDirectionX * force * this.density;
                    this.y -= forceDirectionY * force * this.density;
                } else {
                    if (this.x !== this.baseX) this.x -= (this.x - this.baseX) / 20;
                    if (this.y !== this.baseY) this.y -= (this.y - this.baseY) / 20;
                }
                this.draw();
            }
        }

        const init = () => {
            particlesRef.current = [];
            for (let i = 0; i < particleCount; i++) {
                particlesRef.current.push(new Particle());
            }
        };

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            for (let i = 0; i < particlesRef.current.length; i++) {
                particlesRef.current[i].update();
            }
            animationFrameId = requestAnimationFrame(animate);
        };

        init();
        animate();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            cancelAnimationFrame(animationFrameId);
        };
    }, []); // Empty dependency array means the canvas setup runs once

    // NEW: Watch for activeColor changes and update the existing particles smoothly!
    React.useEffect(() => {
        particlesRef.current.forEach(p => {
            if (p.isThemeColor) {
                p.color = activeColor;
            }
        });
    }, [activeColor]);

    return <canvas ref={canvasRef} className="absolute inset-0 z-0 pointer-events-auto transition-colors duration-1000" />;
};
export default function Portfolio() {
    // 1. Grab the author name from the URL (e.g., "fahad")
    const { author } = useParams(); 

    const [viewState, setViewState] = useState('services');
    const [activeIndex, setActiveIndex] = useState(0);
    const [selectedProject, setSelectedProject] = useState(null);
    const [activeBookProject, setActiveBookProject] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [logoLoaded, setLogoLoaded] = useState(false);

    // 2. FILTERING LOGIC: Check the URL and filter the master SERVICES array
    const filteredServices = React.useMemo(() => {
        let validAuthor = (author && ['hamza', 'fahad', 'asad'].includes(author.toLowerCase())) 
            ? author.toLowerCase() 
            : 'all';

        return SERVICES.map(service => {
            if (service.hasSubMenu) {
                // Keep only sub-projects belonging to the validAuthor
                const filteredSubMenu = service.subMenuData.filter(proj =>
                    validAuthor === 'all' || (proj.authors && proj.authors.includes(validAuthor))
                );
                return { ...service, subMenuData: filteredSubMenu };
            }
            return service;
        }).filter(service => {
            // Remove the main category card if it's empty after filtering
            if (service.hasSubMenu) {
                return service.subMenuData.length > 0;
            }
            // For standalone projects on the main screen
            return validAuthor === 'all' || (service.authors && service.authors.includes(validAuthor));
        });
    }, [author]);

    // 3. Initialize state with the FILTERED list, NOT the master SERVICES list
    const [currentItems, setCurrentItems] = useState(filteredServices);

    // 4. Keep it synced if they click "Back" to the main menu
    React.useEffect(() => {
        if (viewState === 'services') {
           setCurrentItems(filteredServices);
           setActiveIndex(0);
        }
    }, [filteredServices, viewState]);

    // --- CIRCULAR & INFINITE MOBILE SCROLL MATH (BUG FIXED) ---
    const mobileScrollRef = React.useRef(null);
    // ... KEEP THE REST OF YOUR SCROLL MATH CODE FROM HERE DOWN ...
    const cardRefs = React.useRef([]);
    const scrollTimeout = React.useRef(null);

    // Create 3 identical sets of cards for the infinite loop
    const extendedItems = [...currentItems, ...currentItems, ...currentItems];

    // Initial perfect centering effect on load
    React.useEffect(() => {
        const container = mobileScrollRef.current;
        const middleFirstCard = cardRefs.current[currentItems.length]; // The exact 1st card of the middle set
        
        if (container && middleFirstCard) {
            // Disable snapping temporarily to stop the browser from fighting our math
            container.style.scrollSnapType = 'none';
            
            // Mathematically center the exact middle card
            container.scrollLeft = middleFirstCard.offsetLeft - (container.clientWidth / 2) + (middleFirstCard.clientWidth / 2);
            
            // Turn snapping back on after positioning is locked
            setTimeout(() => {
                container.style.scrollSnapType = 'x mandatory';
                handleMobileScroll(); 
            }, 50);
        }
    }, [currentItems, viewState]);

    const handleMobileScroll = () => {
        if (!mobileScrollRef.current) return;
        const container = mobileScrollRef.current;
        const containerCenter = container.scrollLeft + container.clientWidth / 2;
        
        // 1. Calculate the Circular Arc (Fanning out)
        cardRefs.current.forEach((card) => {
            if (!card) return;
            const childCenter = card.offsetLeft + card.clientWidth / 2;
            const distance = childCenter - containerCenter;
            const progress = distance / container.clientWidth; 
            
            const translateY = Math.pow(progress, 2) * 100; // Drops side cards down
            const rotateZ = progress * 20; // Fans side cards outward
            const scale = 1 - Math.min(Math.abs(progress) * 0.15, 0.3); // Shrinks side cards

            card.style.transform = `translateY(${translateY}px) rotateZ(${rotateZ}deg) scale(${scale})`;
            card.style.transformOrigin = "center bottom"; 
        });

        // 2. Infinite Teleportation & Color Sync (Fires when scrolling pauses)
        clearTimeout(scrollTimeout.current);
        scrollTimeout.current = setTimeout(() => {
            if (!cardRefs.current[0] || !cardRefs.current[currentItems.length]) return;
            
            // Perfect mathematical distance of one full set of cards
            const singleSetWidth = cardRefs.current[currentItems.length].offsetLeft - cardRefs.current[0].offsetLeft;
            
            // Find which card is currently in the center of the screen
            let closestIndex = 0;
            let minDistance = Infinity;
            cardRefs.current.forEach((card, index) => {
                if(!card) return;
                const distance = Math.abs((card.offsetLeft + card.clientWidth / 2) - (container.scrollLeft + container.clientWidth / 2));
                if (distance < minDistance) {
                    minDistance = distance;
                    closestIndex = index;
                }
            });
            
            // Update the background/particle color safely
            const realIndex = closestIndex % currentItems.length;
            setActiveIndex((prev) => prev !== realIndex ? realIndex : prev);

            // TELEPORTATION: If user swiped into the Left or Right clone set, teleport them seamlessly to the Middle set
            if (closestIndex < currentItems.length) {
                container.style.scrollSnapType = 'none';
                container.scrollLeft += singleSetWidth;
                setTimeout(() => container.style.scrollSnapType = 'x mandatory', 20);
            } 
            else if (closestIndex >= currentItems.length * 2) {
                container.style.scrollSnapType = 'none';
                container.scrollLeft -= singleSetWidth;
                setTimeout(() => container.style.scrollSnapType = 'x mandatory', 20);
            }
        }, 150); 
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Helper logic
   // --- SAFEGUARD: Prevent spam-clicking/holding Enter from freezing the 3D animations ---
    const isAnimating = React.useRef(false);

    const handleNext = () => {
        if (isAnimating.current) return;
        isAnimating.current = true;
        setActiveIndex((prev) => (prev + 1) % currentItems.length);
        setTimeout(() => { isAnimating.current = false; }, 400); // 400ms cooldown
    };

    const handlePrev = () => {
        if (isAnimating.current) return;
        isAnimating.current = true;
        setActiveIndex((prev) => (prev - 1 + currentItems.length) % currentItems.length);
        setTimeout(() => { isAnimating.current = false; }, 400); // 400ms cooldown
    };
    const handleCardClick = (card, index) => {
        if (index !== activeIndex) {
            setActiveIndex(index);
            return;
        }
        if (card.hasSubMenu) {
            setCurrentItems(card.subMenuData);
            setViewState('projects');
            setActiveIndex(0);
        } else {
            setSelectedProject(card);
        }
    };

    const handleBack = () => {
        setViewState('services');
        setCurrentItems(SERVICES);
        setActiveIndex(0);
    };

    const handleNavClick = (mode) => {
        if (mode === 'Services') handleBack();
        else if (mode === 'About') setViewState('about');
        else if (mode === 'Contact') setViewState('contact');
        else if (mode === 'Profile') setViewState('about');
    };

    // --- CRITICAL SAFETY CHECK FOR RUNTIME ERRORS ---
    const activeCard = currentItems[activeIndex];
    if (!activeCard || !activeCard.hex) return <div className="h-screen bg-black" />;

    return (
        <div id="portfolio-root" className="h-screen bg-[#030303] text-white overflow-hidden relative font-sans selection:bg-white/20 selection:text-white transition-colors duration-1000">

            <AnimatePresence>
                {viewState === 'about' && <AboutOverlay onClose={() => setViewState('services')} />}
                {viewState === 'contact' && <ContactOverlay onClose={() => setViewState('services')} />}

                {/* Standard Project Modal */}
                {selectedProject && (
                    <ProjectModal
                        project={selectedProject}
                        onClose={() => setSelectedProject(null)}
                        onLaunchBook={() => {
                            // If user clicks "Read Article", close modal and open book
                            setSelectedProject(null);
                            setActiveBookProject(selectedProject);
                        }}
                    />
                )}

                {/* Book Reader Overlay */}
                {activeBookProject && (
                    <BookReader
                        project={activeBookProject}
                        onClose={() => setActiveBookProject(null)}
                    />
                )}
            </AnimatePresence>

                
          {/* Background Base */}
            <div className="absolute inset-0 bg-[#030303] z-0"></div>

            {/* --- THE NEW HIGH-DENSITY INTERACTIVE CANVAS PARTICLES --- */}
<ParticleBackground activeColor={activeCard.hex} />

            {/* --- 3D Wavy / Undulating Terrain Grid --- */}
            <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" style={{ perspective: '1000px' }}>
                <div 
                    className="absolute bottom-[-20%] left-[-20%] w-[140%] h-[120%] opacity-[0.15]"
                    style={{
                        transform: 'rotateX(75deg)',
                        transformOrigin: 'bottom center',
                        maskImage: 'linear-gradient(to top, rgba(0,0,0,1) 10%, rgba(0,0,0,0) 80%)',
                        WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,1) 10%, rgba(0,0,0,0) 80%)'
                    }}
                >
                    <svg className="w-full h-full text-white" viewBox="0 0 100 100" preserveAspectRatio="none">
                        {/* Horizontal undulating lines */}
                        {Array.from({ length: 25 }).map((_, i) => (
                            <motion.path
                                key={`h-${i}`}
                                stroke="currentColor"
                                strokeWidth="0.2"
                                fill="none"
                                animate={{
                                    d: [
                                        `M 0 ${i * 4} Q 25 ${i * 4 - 8}, 50 ${i * 4} T 100 ${i * 4}`,
                                        `M 0 ${i * 4} Q 25 ${i * 4 + 8}, 50 ${i * 4} T 100 ${i * 4}`,
                                        `M 0 ${i * 4} Q 25 ${i * 4 - 8}, 50 ${i * 4} T 100 ${i * 4}`
                                    ]
                                }}
                                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
                            />
                        ))}
                        {/* Vertical undulating lines */}
                        {Array.from({ length: 25 }).map((_, i) => (
                            <motion.path
                                key={`v-${i}`}
                                stroke="currentColor"
                                strokeWidth="0.2"
                                fill="none"
                                animate={{
                                    d: [
                                        `M ${i * 4} 0 Q ${i * 4 - 8} 25, ${i * 4} 50 T ${i * 4} 100`,
                                        `M ${i * 4} 0 Q ${i * 4 + 8} 25, ${i * 4} 50 T ${i * 4} 100`,
                                        `M ${i * 4} 0 Q ${i * 4 - 8} 25, ${i * 4} 50 T ${i * 4} 100`
                                    ]
                                }}
                                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
                            />
                        ))}
                    </svg>
                </div>
            </div>

            {/* Dynamic Card Colors (Untouched) */}
            <motion.div key={activeCard.id + "-bg"} animate={{ backgroundColor: activeCard.hex }} transition={{ duration: 1.5 }} className="absolute top-[-20%] left-[-10%] w-[900px] h-[900px] rounded-full blur-[150px] opacity-10 animate-pulse z-0 pointer-events-none will-change-transform" />
            <motion.div key={activeCard.id + "-glow"} animate={{ backgroundColor: activeCard.glowHex }} transition={{ duration: 1.5 }} className="absolute bottom-[-20%] right-[-10%] w-[900px] h-[900px] rounded-full blur-[150px] opacity-10 animate-pulse z-0 pointer-events-none will-change-transform" />
            {/* Nav */}
            <nav className="absolute top-6 left-6 md:top-8 md:left-10 z-[60] flex gap-10 items-center">
                <AnimatePresence mode='wait'>
                    {viewState === 'projects' && (
                        <motion.button
                            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                            onClick={handleBack}
                            className="flex items-center gap-2 text-white/80 hover:text-white uppercase tracking-widest text-[10px] md:text-xs font-bold bg-white/5 px-4 py-2 rounded-full backdrop-blur-md border border-white/10 transition-all hover:bg-white/20 mr-4"
                        >
                            <ArrowLeft size={14} /> Back
                        </motion.button>
                    )}
                </AnimatePresence>
                <div className="hidden md:flex gap-10">
                    {['About', 'Services', 'Contact'].map((item) => (
                        <button key={item} onClick={() => handleNavClick(item)} className="text-gray-400 hover:text-white uppercase tracking-[0.25em] text-xs font-bold relative group transition-colors">
                            {item}<span className="absolute -bottom-2 left-0 w-0 h-[1px] bg-white group-hover:w-full transition-all duration-300"></span>
                        </button>
                    ))}
                </div>
            </nav>

            <div className="absolute top-6 right-6 md:top-8 md:right-10 z-[60]">
                <button onClick={() => handleNavClick('Profile')} className="flex items-center gap-3 bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/30 px-4 py-2 md:px-6 md:py-3 rounded-full transition-all group hover:bg-white/10 shadow-lg">
                    <User size={16} className="text-white/80 group-hover:text-white transition-colors" />
                    <span className="text-xs md:text-sm font-bold tracking-wider text-white/90">My Profile</span>
                </button>
            </div>

            {/* --- LAYOUT ADJUSTMENT: TALLER TOP, SHORTER BOTTOM --- */}
            <div className="relative z-10 flex flex-col h-full w-full">

                {/* 1. TOP SECTION (40% Height) - Shows Logo & Tagline */}
                <div className="relative w-full h-[40%] flex items-center justify-center mt-4">
                    <div className="flex flex-col items-center justify-center cursor-default group">
                        <div className="mb-6 relative w-24 h-24 md:w-32 md:h-32">
                            <div className="absolute inset-0 bg-gradient-to-tr from-blue-500 to-purple-600 rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>

                            {/* Main Logo Skeleton */}
                            {!logoLoaded && <div className="absolute inset-0 bg-white/10 animate-pulse rounded-full z-20" />}

                            <img
                                src="/logo.png"
                                alt="S3 Logo"
                                onLoad={() => setLogoLoaded(true)}
                                className={`w-full h-full object-cover rounded-full border-2 border-white/10 shadow-2xl relative z-10 transform group-hover:scale-105 transition-all duration-500 ${logoLoaded ? 'opacity-100' : 'opacity-0'}`}
                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; setLogoLoaded(true); }}
                            />
                            {/* Fallback Icon */}
                            <div className="hidden w-full h-full bg-gradient-to-tr from-blue-500 to-purple-600 rounded-full items-center justify-center shadow-2xl relative z-10"><Layers size={40} className="text-white" /></div>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-black tracking-tighter text-white text-center leading-none">
                            SYNAPSE <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">TRIO</span>
                        </h1>
                        <p className="text-white/60 text-lg md:text-xl font-light italic mt-2 z-50">Turning Thoughts into Actions.</p>
                    </div>
                </div>

                {/* 2. BOTTOM SECTION (60% Height) - Carousel */}
                {/* 2. BOTTOM SECTION (60% Height) - Carousel */}
                <div className="relative w-full h-[60%] flex flex-col justify-start items-center">
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key={viewState} className="text-white/30 uppercase tracking-[0.3em] text-[10px] font-bold mb-4 absolute top-0 z-50">
                        {viewState === 'services' ? (isMobile ? 'Swipe Domains' : 'Select Domain') : 'Select Project for Details'}
                    </motion.div>

                    {/* === DESKTOP 3D CAROUSEL (Hidden on Mobile) === */}
                    <div className="hidden md:flex relative w-full max-w-[1400px] h-full justify-center items-center perspective-1000 pb-8 mt-4">
                        <AnimatePresence mode='popLayout'>
                            {currentItems.map((card, index) => {
                                const offset = (index - activeIndex + currentItems.length) % currentItems.length;
                                let zIndex = 0, scale = 0.8, opacity = 0, x = 0, rotateY = 0, isFront = false;
                                const desktopOffset = 380;

                                if (offset === 0) { zIndex = 150; scale = 1; opacity = 1; x = 0; isFront = true; }
                                else if (offset === 1) { zIndex = 140; scale = 0.8; opacity = 0.5; x = desktopOffset; rotateY = -25; }
                                else if (offset === currentItems.length - 1) { zIndex = 140; scale = 0.8; opacity = 0.5; x = -desktopOffset; rotateY = 25; }
                                else { zIndex = 110; opacity = 0; scale = 0.5; }

                                return (
                                    <motion.div
                                        key={card.id} layout
                                        className="absolute top-auto h-[410px] w-[350px] cursor-pointer"
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        animate={{ x, scale, opacity, zIndex, rotateY }}
                                        exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
                                        transition={{ type: "spring", stiffness: 120, damping: 20, mass: 1 }}
                                        onClick={() => handleCardClick(card, index)}
                                        style={{ transformStyle: 'preserve-3d' }}
                                    >
                                        <div className="h-full w-full rounded-[2rem] p-8 flex flex-col justify-between relative overflow-hidden bg-white/5 backdrop-blur-2xl border border-white/10 transition-all duration-500 group"
                                            style={{ boxShadow: isFront ? `0 20px 50px -10px ${card.hex}40, 0 0 20px ${card.hex}20` : 'none' }}>
                                            <div className="absolute inset-0 rounded-[2rem] border border-transparent transition-colors duration-300 pointer-events-none" style={{ borderColor: isFront ? `${card.hex}50` : 'transparent' }}></div>
                                            <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b opacity-20 pointer-events-none" style={{ backgroundImage: `linear-gradient(to bottom, ${card.hex}, transparent)` }}></div>

                                            <div className="relative z-10 flex flex-col h-full">
                                                <div className="w-20 h-20 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center mb-6 shadow-lg shrink-0 backdrop-blur-md">
                                                    {card.icon}
                                                </div>
                                                <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar leading-relaxed">
                                                    <h2 className="text-3xl font-bold text-white mb-2 tracking-tight">{card.title}</h2>
                                                    <p className="text-gray-300 text-base font-light">{card.desc}</p>
                                                </div>
                                                <div className="mt-6 shrink-0">
                                                    {isFront ? (
                                                        <button className="w-full py-5 rounded-xl font-bold text-black text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] active:scale-[0.98] shadow-xl"
                                                            style={{ background: `linear-gradient(135deg, ${card.glowHex}, ${card.hex})` }}>
                                                            {card.hasSubMenu ? <>Explore Projects <Layers size={16} /></> :
                                                                (card.type === 'article' ? <>Read Article <BookOpen size={16} /></> :
                                                                    (card.link ? <>View & Launch <ArrowRight size={16} /></> : <>View Details <ArrowRight size={16} /></>))
                                                            }
                                                        </button>
                                                    ) : (
                                                        <div className="w-full py-3 border border-white/10 rounded-xl text-center text-white/20 text-[10px] uppercase tracking-widest font-bold">Select</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                        <button onClick={handlePrev} className="absolute left-8 z-50 p-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 backdrop-blur-md text-white/70 hover:text-white transition-all hover:scale-110 active:scale-95"><ChevronRight className="rotate-180 w-6 h-6" /></button>
                        <button onClick={handleNext} className="absolute right-8 z-50 p-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 backdrop-blur-md text-white/70 hover:text-white transition-all hover:scale-110 active:scale-95"><ChevronRight className="w-6 h-6" /></button>
                    </div>

                    {/* === MOBILE NATIVE SNAP CAROUSEL (Visible only on Mobile) === */}
                   {/* === CIRCULAR & INFINITE MOBILE NATIVE SNAP CAROUSEL === */}
                    <div 
                        ref={mobileScrollRef}
                        onScroll={handleMobileScroll}
                        className="md:hidden w-full h-full flex overflow-x-auto snap-x snap-mandatory px-[7.5%] gap-4 pb-20 pt-8 items-center hide-scrollbar"
                    >
                        {extendedItems.map((card, index) => {
                            const realIndex = index % currentItems.length;
                            return (
                                <div 
                                    key={`${index}-${card.id}`}
                                    ref={el => cardRefs.current[index] = el}
                                    onClick={() => handleCardClick(card, realIndex)}
                                    className="snap-center shrink-0 w-[85%] min-h-[400px] h-[85%] bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[2rem] p-6 flex flex-col relative shadow-2xl active:scale-[0.98] will-change-transform"
                                >
                                    {/* Hide overflow for the background gradient, but not the card itself so the 3D popout works */}
                                    <div className="absolute inset-0 rounded-[2rem] overflow-hidden pointer-events-none z-0">
                                        <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b opacity-20 pointer-events-none" style={{ backgroundImage: `linear-gradient(to bottom, ${card.hex}, transparent)` }}></div>
                                    </div>
                                    
                                    <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center mb-4 shadow-lg shrink-0 backdrop-blur-md relative z-10">
                                        {card.icon}
                                    </div>
                                    <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar relative z-10">
                                        <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">{card.title}</h2>
                                        <p className="text-gray-300 text-sm font-light leading-relaxed">{card.desc}</p>
                                    </div>
                                    <div className="mt-4 shrink-0 relative z-10">
                                        <button className="w-full py-4 min-h-[44px] rounded-xl font-bold text-black text-sm uppercase tracking-wider flex items-center justify-center gap-2 shadow-xl"
                                            style={{ background: `linear-gradient(135deg, ${card.glowHex}, ${card.hex})` }}>
                                            {card.hasSubMenu ? <>Explore <Layers size={16} /></> :
                                                (card.type === 'article' ? <>Read <BookOpen size={16} /></> : <>View <ArrowRight size={16} /></>)
                                            }
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {/* === MOBILE BOTTOM NAVIGATION === */}
            {isMobile && (
                <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-[90] flex items-center gap-6 bg-black/80 backdrop-blur-xl border border-white/10 px-6 py-3 rounded-full shadow-2xl">
                    <button 
                        onClick={() => handleNavClick('Services')} 
                        className={`p-2 rounded-full transition-all ${viewState === 'services' || viewState === 'projects' ? 'text-blue-400 scale-125' : 'text-white/50'}`}
                    >
                        <Layers size={24} />
                    </button>
                    
                    <button 
                        onClick={() => handleNavClick('About')} 
                        className={`p-2 rounded-full transition-all ${viewState === 'about' ? 'text-blue-400 scale-125' : 'text-white/50'}`}
                    >
                        <User size={24} />
                    </button>
                    
                    <button 
                        onClick={() => handleNavClick('Contact')} 
                        className={`p-2 rounded-full transition-all ${viewState === 'contact' ? 'text-blue-400 scale-125' : 'text-white/50'}`}
                    >
                        <Mail size={24} />
                    </button>
                </div>
            )}
            <style>{`.custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.05); } .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.2); border-radius: 10px; } .perspective-1000 { perspective: 1000px; }`}</style>
        </div>
    );
}